// invodiff-jobs.jsx — analytics dashboard landing page
import { PriceHistoryView } from './price-history.jsx';
import React from 'react';
import { firebase, fbAuth, fbDb, fbStorage } from './firebase.js';
import {
  fmtUSD, fmtDelta, safeUrl, firestoreSafe, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  ROLES, ROLE_LABEL, ROLE_COLOR,
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep, useVendorRules,
  normalizeUOM, isFootageUOM, stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  aggregateInvoices, compareInvoices, materialState, classifyRows, MATERIAL_STATE_META,
  getJobTrades, getSegments, lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
} from './invodiff-data.jsx';
import { Icon } from './invodiff-input.jsx';
import { BudgetPanel } from './invodiff-compare.jsx';

// ── Toast system ──────────────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = React.useState([]);
  const addToast = React.useCallback((msg, tone = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, msg, tone }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 2800);
  }, []);
  return { toasts, addToast };
}

function ToastContainer({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div className="toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.tone}`}>{t.msg}</div>
      ))}
    </div>
  );
}

// Lightweight per-browser preference store for the dashboard (view mode, sort).
// Wrapped in try/catch — localStorage throws in private mode / when disabled.
function readPref(key, fallback) {
  try { return localStorage.getItem(`kcg.jobs.${key}`) || fallback; }
  catch { return fallback; }
}
function writePref(key, value) {
  try { localStorage.setItem(`kcg.jobs.${key}`, value); } catch { /* ignore */ }
}

// Compute per-job summary stats from Firestore job data.
// vendorRules are passed in so resolved/accepted issues are honored here exactly
// as on the job's Issues page — the flag badge must reconcile with that screen.
function computeJobStats(job, vendorRules) {
  const invoices = (job.invoices || []).filter((inv) => !inv._hidden);
  const taxExempt = !!job.taxExempt;
  // Quoted baseline excludes tax on a tax-exempt job (subtotal + shipping).
  const quotedBaseline = (q) => taxExempt ? +(subtotal(q) + (q.shipping || 0)).toFixed(2) : grandTotal(q);

  // Roll the card up across every trade segment. Single-trade / untagged jobs
  // collapse to one segment over the legacy quote, so the numbers below are
  // identical to the pre-multi-trade behavior; multi-trade jobs sum each trade's
  // own quote-vs-invoice comparison so the dashboard tile reflects the whole job.
  const segments     = getSegments({ ...job, invoices }); // all trades
  const quoteSegs    = segments.filter((s) => s.quote);
  if (quoteSegs.length === 0) {
    return { hasData: false, vendor: '', invoiceCount: invoices.length };
  }
  // Distinct vendors across the priced trades (one entry for single-trade jobs,
  // so the value is the same string as before).
  const vendor = Array.from(new Set(quoteSegs.flatMap((s) => s.quote.vendor ? [s.quote.vendor] : []))).join(', ');
  const anyInv = segments.some((s) => s.invoices.length > 0);

  if (!anyInv) {
    const quotedTotal = +quoteSegs.reduce((sum, s) => sum + quotedBaseline(s.quote), 0).toFixed(2);
    return {
      hasData: true, noBill: true,
      vendor,
      invoiceCount: 0,
      quotedTotal,
      invoicedTotal: 0,
      remaining: quotedTotal,
      pctUsed: 0,
      flaggedCount: 0,
      lfQuoted: 0, lfInvoiced: 0,
      status: 'pending',
    };
  }

  try {
    let quotedTotal = 0, invoicedTotal = 0, lfQuoted = 0, lfInvoiced = 0;
    let open = 0, problem = 0, review = 0;
    for (const seg of quoteSegs) {
      // A priced trade with no invoices yet still counts its quote toward budget.
      if (seg.invoices.length === 0) { quotedTotal += quotedBaseline(seg.quote); continue; }
      const rules = (vendorRules || {})[seg.quote.vendor || ''] || [];
      const { summary, rows } = aggregateInvoices(seg.quote, seg.invoices, rules, { taxExempt });
      // Footage running total. lfInvoiced counts ALL footage rows incl. off-quote
      // "added" lines — a size swap / substitution invoiced off-quote still draws
      // down real footage. lfQuoted stays the quote baseline (added lines carry no
      // quotedLF, so they add to invoiced only). quotedLF/invoicedLF also fold in
      // stick-derived footage (LN × stick length), matching the budget panel.
      const lfRows = rows.filter((r) => !r.synthetic
        && (r.quotedLF != null || r.invoicedLF != null || r.uomNorm === 'LF'));
      quotedTotal   += summary.quotedTotal;
      invoicedTotal += summary.invoicedTotal;
      lfQuoted      += lfRows.reduce((s, r) => s + (r.quotedLF   ?? (r.uomNorm === 'LF' ? r.quotedQty   || 0 : 0)), 0);
      lfInvoiced    += lfRows.reduce((s, r) => s + (r.invoicedLF ?? (r.uomNorm === 'LF' ? r.invoicedQty || 0 : 0)), 0);
      // Open issues = the SAME problem+review count the job's Issues page shows,
      // honoring accepted/ignored rules — not aggregateInvoices' raw flaggedCount.
      const c = classifyRows(rows, job.tolerances);
      open    += c.open;
      problem += c.count.problem;
      review  += c.count.review;
    }
    quotedTotal   = +quotedTotal.toFixed(2);
    invoicedTotal = +invoicedTotal.toFixed(2);
    const overBudget = invoicedTotal > quotedTotal;
    return {
      hasData: true,
      vendor,
      invoiceCount: invoices.length,
      quotedTotal,
      invoicedTotal,
      remaining: +(quotedTotal - invoicedTotal).toFixed(2),
      pctUsed: quotedTotal > 0 ? invoicedTotal / quotedTotal : 0,
      flaggedCount: open,
      problemCount: problem,
      reviewCount: review,
      lfQuoted, lfInvoiced,
      overBudget,
      // Over budget outranks a clean issue list. Spend can exceed the quote with
      // zero OPEN issues — classifyRows only sees real rows, so shipping, tax and
      // footer adjustments never count, and an accepted vendor rule settles the
      // issue without refunding the money. `status` drives both the badge text
      // and the budget-bar color, so gating on `open` alone painted a green
      // "On track" bar over a job that was thousands over.
      status: (overBudget || problem > 0) ? 'bad' : open === 0 ? 'ok' : 'warn',
    };
  } catch {
    return { hasData: false, vendor, invoiceCount: invoices.length };
  }
}

// Portfolio rollup across the ACTIVE jobs — the metric row at the top of the
// dashboard. Pure over the per-job stats map, so it is only ever as right as
// computeJobStats above; jobs with no entry in the map (still loading) are
// skipped rather than counted as zero, which would dilute the totals mid-load.
//
// Money is summed from values computeJobStats already rounded to the cent, so
// `budgetTotal`/`invoicedTotal` can carry float dust; fmtUSD renders at 2dp so
// it never reaches a user. The two DERIVED figures are re-rounded because they
// are subtractions, where the dust would otherwise be visible against a
// hand-checked total.
// ── Session-wide job stats cache ───────────────────────────────────────────
// computeJobStats runs the matcher over a job's whole quote and every invoice,
// which is the single most expensive thing the global screens do. The Dashboard
// and the Jobs list are separate mounts of JobListView, so a cache held inside
// the component died on every tab switch and each switch re-matched the entire
// portfolio — seconds of frozen UI on a real job list. This one lives at module
// scope and so lasts the session, like the jobs store it is keyed on.
//
// Keyed on the job OBJECT: the store guarantees an untouched job keeps its
// reference (DECISIONS D-011), so identity is exactly "nothing about this job
// changed", and a WeakMap lets a replaced or deleted job's entry be collected.
// An entry is also void once the vendor-rules object differs, because rules
// change which rows are resolved. Each job keeps a few date windows side by
// side so the ranged Dashboard and the un-ranged Jobs list don't evict each
// other.
const _jobStatsCache = new WeakMap(); // job → { rules, byRange: Map(rangeKey → stats) }
const JOB_STATS_RANGES_KEPT = 4;

// range: null for the whole job, or { from, to } for a date window.
const jobStatsRangeKey = (range) => (range ? `${range.from}|${range.to}` : '');

// The stats for one job: the cached object when this exact job / rules / window
// has been computed before, otherwise a fresh computation. Read-only — safe to
// call during render.
function jobStatsFor(job, vendorRules, range) {
  const hit = _jobStatsCache.get(job);
  const cached = hit && hit.rules === vendorRules ? hit.byRange.get(jobStatsRangeKey(range)) : undefined;
  if (cached) return cached;
  // A window filters invoices BEFORE the stats engine runs, so invoiced /
  // footage / flag counts all reflect it; the quote baseline is undated.
  const scoped = range
    ? { ...job, invoices: (job.invoices || []).filter((inv) => invoiceInRange(inv, range)) }
    : job;
  return computeJobStats(scoped, vendorRules);
}

// Record stats so the next jobStatsFor with the same job / rules / window
// returns them. Called from an effect, never during render.
function rememberJobStats(job, vendorRules, range, stats) {
  if (!job || !stats) return;
  let hit = _jobStatsCache.get(job);
  if (!hit || hit.rules !== vendorRules) {
    hit = { rules: vendorRules, byRange: new Map() };
    _jobStatsCache.set(job, hit);
  }
  const key = jobStatsRangeKey(range);
  hit.byRange.delete(key);            // re-insert so the newest window is last
  hit.byRange.set(key, stats);
  while (hit.byRange.size > JOB_STATS_RANGES_KEPT) hit.byRange.delete(hit.byRange.keys().next().value);
}

function computePortfolioMetrics(activeJobs, jobStats) {
  const all = (activeJobs || []).flatMap((j) => {
    const st = (jobStats || {})[j.id];
    return st ? [st] : [];
  });
  const budgetTotal   = all.reduce((s, st) => s + (st.quotedTotal   || 0), 0);
  const invoicedTotal = all.reduce((s, st) => s + (st.invoicedTotal || 0), 0);
  return {
    totalJobs:    (activeJobs || []).length,
    budgetTotal,
    invoicedTotal,
    remaining:    +(budgetTotal - invoicedTotal).toFixed(2),
    // Exposure = sum of the per-job overage only (under-budget jobs don't net it
    // out), so it reads as true dollars-at-risk across the portfolio.
    overExposure: +all.reduce((s, st) => s + Math.max(0, (st.invoicedTotal || 0) - (st.quotedTotal || 0)), 0).toFixed(2),
    overJobs:     all.filter((st) => (st.invoicedTotal || 0) > (st.quotedTotal || 0)).length,
    lfTracked:    all.reduce((s, st) => s + (st.lfInvoiced    || 0), 0),
    needsReview:  all.filter((st)    => st.flaggedCount > 0).length,
    invoiceCount: all.reduce((s, st) => s + (st.invoiceCount  || 0), 0),
  };
}

// Client-side sort over the active job list. 'recent' keeps Firestore's
// createdAt-desc order untouched (and returns the SAME array, so the memo
// downstream stays identity-stable); every other mode re-ranks by the per-job
// stats so a PM can triage by spend or risk. Never mutates the input.
function sortJobs(list, sortBy, jobStats) {
  if (sortBy === 'recent') return list;
  const st  = (j) => (jobStats || {})[j.id] || {};
  const arr = [...list];
  switch (sortBy) {
    case 'number':    arr.sort((a, b) => (a.jobNumber || '').localeCompare(b.jobNumber || '', undefined, { numeric: true })); break;
    case 'usage':     arr.sort((a, b) => (st(b).pctUsed      || 0) - (st(a).pctUsed      || 0)); break;
    case 'flags':     arr.sort((a, b) => (st(b).flaggedCount || 0) - (st(a).flaggedCount || 0)); break;
    // least remaining (most over budget) first; jobs with no data sink to the bottom.
    case 'remaining': arr.sort((a, b) => (st(a).remaining ?? Infinity) - (st(b).remaining ?? Infinity)); break;
    default: break;
  }
  return arr;
}

// ── Dashboard metric card ─────────────────────────────────────────────────
function DashMetric({ label, value, sub, tone = 'default', icon, onClick, active, cta }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`dm dm-${tone}${clickable ? ' dm-clickable' : ''}${active ? ' dm-active' : ''}`}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable && active != null ? active : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="dm-top">
        <div className="dm-label">{label}</div>
        <div className="dm-icon"><Icon name={icon} size={15} /></div>
      </div>
      <div className="dm-value num">{value}</div>
      {sub && <div className="dm-sub">{sub}</div>}
      {clickable && cta && (
        <div className="dm-cta">{cta} <Icon name="chev" size={11} /></div>
      )}
    </div>
  );
}

// Pure helpers shared by the job card + table row. They depend only on their
// arguments, so they live at module scope rather than being rebuilt each render.
const fmtDate = (ts) => {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
// Coerce an ISO string / Firestore Timestamp / Date to epoch ms for sorting.
const tsMillis = (x) => {
  if (!x) return 0;
  if (typeof x === 'string') { const d = new Date(x); return isNaN(d.getTime()) ? 0 : d.getTime(); }
  if (x.toDate) return x.toDate().getTime();
  if (x instanceof Date) return x.getTime();
  return 0;
};
const stopAndCall = (e, fn) => { e.stopPropagation(); fn(); };

// Generic field updater for the edit drawer (trades, etc.). Replaces the named
// fields on the job doc; map fields are overwritten wholesale (no deep merge).
// Pure relative to component scope (only the global fbDb), so it lives here.
const updateJobFields = async (job, fields) => {
  try {
    await fbDb.collection('jobs').doc(job.id).update(fields);
  } catch (e) {
    console.error('Failed to update job:', e);
  }
};

// Shared status → color/label maps for the job list. Static (no local state),
// so they live at module scope and stay stable across renders. Cards have room
// for fuller copy than the dense table rows, so the two label maps differ.
const JOB_STATUS_COLOR      = { ok: 'var(--ok)', warn: 'var(--warn)', bad: 'var(--bad)', pending: 'var(--fg-dim)' };
const JOB_CARD_STATUS_LABEL = { ok: 'On track', warn: 'Review needed', bad: 'Over budget', problem: 'Problems found', pending: 'No invoices yet' };
const JOB_ROW_STATUS_LABEL  = { ok: 'On track', warn: 'Review', bad: 'Over budget', problem: 'Problems', pending: 'No invoices' };

// The red state has two distinct causes, and they are not the same sentence to
// a PM: either the job's spend passed its quote, or a line item is a problem
// while the job is still under budget. Both are red — but calling the second one
// "Over budget" tells the user something untrue about their job, and it is the
// kind of untrue thing that gets forwarded to a vendor. Color comes from
// `status`; the words come from `overBudget`.
const jobStatusLabel = (s, labels) => (
  s.status === 'bad' && !s.overBudget ? labels.problem : labels[s.status]
);

// One CSV row per job — the numbers that leave the app and land in somebody's
// spreadsheet, so they are raw (no $ or commas) and must line up column-for-
// column with JOB_CSV_HEADERS.
const JOB_CSV_HEADERS = ['Job #', 'Name', 'Vendor', 'Trades', 'Quoted', 'Invoiced',
  'Remaining', '% Used', 'Status', 'Open Flags', 'Invoices', 'LF Invoiced'];

function jobCSVRow(job, stats, isRanged) {
  const s = stats || {};
  return [
    job.jobNumber || '',
    job.jobName   || '',
    s.vendor      || '',
    (job.trades || []).join('; '),
    s.quotedTotal   != null ? s.quotedTotal   : '',
    s.invoicedTotal != null ? s.invoicedTotal : '',
    // Remaining / % are whole-job ratios — blanked when a window is active
    // (same reason the UI hides them), since invoiced is windowed but the
    // quote is not.
    isRanged ? '' : (s.remaining != null ? s.remaining : ''),
    isRanged ? '' : (s.pctUsed   != null ? Math.round(s.pctUsed * 100) : ''),
    s.hasData ? (jobStatusLabel(s, JOB_ROW_STATUS_LABEL) || '') : 'No data',
    s.flaggedCount || 0,
    s.invoiceCount || 0,
    s.lfInvoiced   || 0,
  ];
}

// Stand-in for the jobs list while the store is still loading. One shared
// array, not a fresh `[]` per render — every memo below keys on `jobs`, and a
// new identity each render would recompute all of them for nothing.
const NO_JOBS = [];

// ── Trades / categories ────────────────────────────────────────────────────
// Tag jobs by the trade(s) they cover so the job viewer can be filtered. A job
// can carry several (e.g. piping + insulation). Presets cover the common ones;
// custom values are allowed and persist on the job.
const PRESET_TRADES = ['Piping', 'Plumbing', 'HVAC', 'Sheet Metal', 'Insulation', 'Electrical', 'Fire Protection', 'Controls', 'Demo', 'General'];

// Read-only trade pills (job cards / rows). When onClick is given, pills act as
// quick filters.
function TradeChips({ trades, onClick, active, max }) {
  if (!trades || trades.length === 0) return null;
  const shown = max ? trades.slice(0, max) : trades;
  const extra = max ? trades.length - shown.length : 0;
  return (
    <div className="jl-trades">
      {shown.map((t) => (
        <span key={t} className={`jl-trade${active === t ? ' is-active' : ''}${onClick ? ' is-click' : ''}`}
          {...(onClick ? {
            role: 'button', tabIndex: 0, 'aria-pressed': active === t,
            onClick: (e) => { e.stopPropagation(); onClick(t); },
            onKeyDown: (e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault(); e.stopPropagation(); onClick(t);
            },
          } : {})}>
          {t}
        </span>
      ))}
      {extra > 0 && <span className="jl-trade jl-trade-more">+{extra}</span>}
    </div>
  );
}

// Editable multi-select of trades for the job drawers.
function TradePicker({ value, onChange }) {
  const [custom, setCustom] = React.useState('');
  const selected = new Set(value || []);
  const toggle = (t) => {
    const next = new Set(value || []);
    if (next.has(t)) next.delete(t); else next.add(t);
    onChange([...next]);
  };
  const addCustom = () => {
    const t = custom.trim();
    if (t && !selected.has(t)) onChange([...(value || []), t]);
    setCustom('');
  };
  const options = [...PRESET_TRADES, ...(value || []).filter((t) => !PRESET_TRADES.includes(t))];
  return (
    <div className="jed-trades">
      <div className="jed-trade-chips">
        {options.map((t) => (
          <button key={t} type="button" className={`jed-trade-chip${selected.has(t) ? ' is-on' : ''}`} onClick={() => toggle(t)}>
            {selected.has(t) && <Icon name="check" size={10} />} {t}
          </button>
        ))}
      </div>
      <div className="jed-trade-add">
        <input className="input" value={custom} placeholder="Add a custom trade…" aria-label="Custom trade"
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} />
        <button className="btn btn-sm" type="button" onClick={addCustom} disabled={!custom.trim()}>Add</button>
      </div>
    </div>
  );
}

// Job-page scope selector — only meaningful when a job covers 2+ trades. Lets
// the user view one trade in isolation or a mix; `selected === null` means all.
// Each trade is its own quote-vs-invoices comparison; this just chooses which
// segments the tabs show. Renders nothing for single-trade jobs (the default).
function TradeScopeSelector({ trades, selected, onChange }) {
  if (!trades || trades.length < 2) return null;
  const all = !selected || selected.length === 0 || selected.length === trades.length;
  const toggle = (t) => {
    // Chips switch the view like radio buttons: clicking a trade shows just that
    // trade, so Piping → Insulation goes straight there. Clicking the trade
    // that's already the sole view returns to "All trades".
    if (!all && selected.length === 1 && selected[0] === t) { onChange(null); return; }
    onChange([t]);
  };
  return (
    <div className="jts">
      <span className="jts-label">View</span>
      <div className="jts-chips">
        <button type="button" className={`jts-chip${all ? ' is-on' : ''}`} onClick={() => onChange(null)}>
          All trades
        </button>
        {trades.map((t) => (
          <button key={t} type="button"
            className={`jts-chip${!all && selected.includes(t) ? ' is-on' : ''}`}
            onClick={() => toggle(t)}>
            {t}
          </button>
        ))}
      </div>
      <TradeScopeStyles />
    </div>
  );
}

// Styles travel with the selector — it renders on the job-detail page, where the
// job-list stylesheet isn't mounted, so it must carry its own.
const TradeScopeStyles = () => (
  <style>{`
    .jts { display: flex; align-items: center; gap: 8px; margin-left: auto; padding: 7px 0; flex-shrink: 0; }
    .jts-label { font-size: 10px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: var(--fg-dim); }
    .jts-chips { display: flex; flex-wrap: wrap; gap: 5px; }
    .jts-chip {
      appearance: none; cursor: pointer; font: inherit;
      font-size: 11.5px; font-weight: 500; line-height: 1;
      padding: 5px 11px; border-radius: 999px;
      border: 1px solid var(--border); background: var(--surface-2); color: var(--fg-muted);
      transition: background .12s, color .12s, border-color .12s;
    }
    .jts-chip:hover { background: var(--surface-3); color: var(--fg); }
    .jts-chip.is-on {
      background: oklch(from var(--accent) l c h / .14); color: var(--accent);
      border-color: oklch(from var(--accent) l c h / .45);
    }
    @media (max-width: 640px) { .jts { margin-left: 0; width: 100%; padding-bottom: 9px; } }
  `}</style>
);

// Heading that precedes one trade's section on the multi-trade Summary /
// Materials / Issues screens. Inline styles (hoisted) so it needs no per-file
// CSS — it renders identically inside all three views.
const TS_HD_STYLE   = { display: 'flex', alignItems: 'center', gap: '10px', margin: '20px 0 2px', paddingBottom: '10px', borderBottom: '1px solid var(--border)' };
const TS_DOT_STYLE  = { width: '9px', height: '9px', borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 };
const TS_NAME_STYLE = { fontSize: '15px', fontWeight: 700, letterSpacing: '-0.01em' };
const TS_SUB_STYLE  = { fontSize: '12px', color: 'var(--fg-dim)', marginLeft: 'auto' };
function TradeSectionHeader({ trade, sub }) {
  return (
    <div style={TS_HD_STYLE}>
      <span style={TS_DOT_STYLE} />
      <span style={TS_NAME_STYLE}>{trade}</span>
      {sub && <span style={TS_SUB_STYLE}>{sub}</span>}
    </div>
  );
}

// Shared in-app confirmation dialog — replaces native window.confirm() across
// screens so prompts match the app instead of a jarring browser popup. Driven
// by a cfg object: { title, body, confirmLabel, tone('bad'|'warn'), icon, onConfirm }.
// Self-styled (injects its own CSS) so it works on any screen.
function ConfirmModal({ cfg, onClose }) {
  const confirmRef = React.useRef(null);
  React.useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const tone = cfg.tone || 'bad';
  return (
    <div className="cfm-overlay" onClick={onClose}>
      <div className="cfm-dialog" role="dialog" aria-modal="true" aria-label={cfg.title}
           onClick={(e) => e.stopPropagation()}>
        <div className={`cfm-icon cfm-icon-${tone}`}><Icon name={cfg.icon || 'trash'} size={20} /></div>
        <div className="cfm-title">{cfg.title}</div>
        <div className="cfm-body">{cfg.body}</div>
        <div className="cfm-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button ref={confirmRef} className={`cfm-confirm cfm-confirm-${tone}`}
                  onClick={() => { cfg.onConfirm(); onClose(); }}>
            {cfg.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
      <ConfirmModalStyles />
    </div>
  );
}

const ConfirmModalStyles = () => (
  <style>{`
    .cfm-overlay {
      position: fixed; inset: 0; background: oklch(0 0 0 / 0.45);
      display: grid; place-items: center; z-index: 9000; animation: cfm-fade .15s ease;
    }
    @keyframes cfm-fade { from { opacity: 0; } to { opacity: 1; } }
    .cfm-dialog {
      background: var(--surface); border: 1px solid var(--border);
      border-radius: var(--r-lg); box-shadow: var(--shadow-2);
      padding: 26px 26px 22px; width: min(420px, calc(100vw - 32px));
      display: flex; flex-direction: column; gap: 12px; animation: cfm-up .15s ease;
    }
    @keyframes cfm-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
    .cfm-icon { width: 44px; height: 44px; border-radius: 10px; display: grid; place-items: center; }
    .cfm-icon-bad  { background: var(--bad-bg);  color: var(--bad);  border: 1px solid oklch(from var(--bad)  l calc(c*.5) h / .3); }
    .cfm-icon-warn { background: var(--warn-bg); color: var(--warn); border: 1px solid oklch(from var(--warn) l calc(c*.5) h / .3); }
    .cfm-title { font-size: 16px; font-weight: 600; letter-spacing: -0.01em; }
    .cfm-body  { font-size: 13px; color: var(--fg-muted); line-height: 1.55; }
    .cfm-body strong { color: var(--fg); font-weight: 600; }
    .cfm-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 4px; }
    .cfm-confirm {
      appearance: none; border: 1px solid; border-radius: var(--r); padding: 7px 16px;
      font: inherit; font-size: 13px; font-weight: 500; cursor: pointer;
      transition: background .12s, border-color .12s;
    }
    .cfm-confirm-bad  { border-color: oklch(from var(--bad)  l calc(c*.5) h / .5); background: var(--bad-bg);  color: var(--bad);  }
    .cfm-confirm-bad:hover  { background: oklch(from var(--bad)  l c h / 0.18); border-color: var(--bad);  }
    .cfm-confirm-warn { border-color: oklch(from var(--warn) l calc(c*.5) h / .5); background: var(--warn-bg); color: var(--warn); }
    .cfm-confirm-warn:hover { background: oklch(from var(--warn) l c h / 0.18); border-color: var(--warn); }
    .cfm-confirm:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  `}</style>
);

// ── Job card ──────────────────────────────────────────────────────────────
function JobCard({ job, stats, onClick, onToggleActive, onArchive, onDelete, onEdit, procoreCompanyId, ranged }) {
  const s           = stats;
  const isArchived  = job.active === false;
  const pctClamped  = s ? Math.min(s.pctUsed, 1) : 0;
  const statusColor = s ? JOB_STATUS_COLOR[s.status] : 'var(--fg-dim)';

  const procoreUrl = job.procoreUrl ||
    (job.procoreProjectId && procoreCompanyId
      ? `https://sandbox.procore.com/webclients/host/companies/${procoreCompanyId}/projects/${job.procoreProjectId}/tools/projecthome`
      : null);

  return (
    <div
      className={`jl-card card${s?.flaggedCount > 0 && !isArchived ? ' jl-card-flagged' : ''}${isArchived ? ' jl-card-archived' : ''}`}
      onClick={onClick} role="button" tabIndex={0}
      /* UX #14 (a11y): native buttons fire on Space too — match that here.
         Only when the card itself has focus: Enter on the Edit / Archive /
         Delete buttons inside it bubbles up here, and without this check it
         opened the job and swallowed the button's own action. */
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
    >
      <div className="jl-card-top">
        <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <div className="jl-card-num mono">{job.jobNumber}</div>
          {job.jobName && <div className="jl-card-name">{job.jobName}</div>}
        </div>
        <div className="jl-card-top-right">
          {isArchived ? (
            <span className="jl-archived-tag">Archived</span>
          ) : (
            s && (
              <span className="jl-card-status">
                <span className="jl-cs-dot" style={{ background: statusColor }} />
                <span style={{ color: statusColor }}>{jobStatusLabel(s, JOB_CARD_STATUS_LABEL)}</span>
              </span>
            )
          )}
          {/* UX #1 (a11y): icon-only buttons need an accessible name, not just title. */}
          <div className="jl-card-acts" onClick={(e) => e.stopPropagation()}>
            <button className="jl-act-btn" title="Edit job" aria-label="Edit job" onClick={(e) => stopAndCall(e, onEdit)}>
              <Icon name="pencil" size={13} />
            </button>
            <button className="jl-act-btn" title={isArchived ? 'Restore job' : 'Archive job'} aria-label={isArchived ? 'Restore job' : 'Archive job'}
              onClick={(e) => stopAndCall(e, isArchived ? onToggleActive : onArchive)}>
              <Icon name={isArchived ? 'eye' : 'archive'} size={13} />
            </button>
            <button className="jl-act-btn jl-act-del" title="Delete job permanently" aria-label="Delete job permanently" onClick={(e) => stopAndCall(e, onDelete)}>
              <Icon name="trash" size={13} />
            </button>
          </div>
        </div>
      </div>

      {s?.vendor && <div className="jl-card-vendor">{s.vendor}</div>}

      {job.trades?.length > 0 && <TradeChips trades={job.trades} max={3} />}

      {/* Ranged: a quote isn't dated, so the %/budget-bar are meaningless for a
          window — show the windowed invoiced spend instead. */}
      {s?.hasData && !isArchived && ranged && (
        <div className="jl-card-quoted">
          <span className="jl-cq-label">Invoiced</span>
          <span className="num">{fmtUSD(s.invoicedTotal || 0)}</span>
        </div>
      )}
      {s?.hasData && !s?.noBill && !isArchived && !ranged && (
        <>
          <div className="jl-budget-bar">
            <div className="jl-bb-track">
              <div className="jl-bb-fill" style={{ width: `${pctClamped * 100}%`, background: statusColor }} />
            </div>
            <span className="jl-bb-pct num" style={{ color: statusColor }}>
              {Math.round(s.pctUsed * 100)}%
            </span>
          </div>
          <div className="jl-card-amounts">
            <span className="num jl-ca-inv">{fmtUSD(s.invoicedTotal)}</span>
            <span className="jl-ca-sep">of</span>
            <span className="num jl-ca-qtd">{fmtUSD(s.quotedTotal)}</span>
          </div>
        </>
      )}
      {s?.hasData && s?.noBill && !ranged && (
        <div className="jl-card-quoted">
          <span className="jl-cq-label">Quoted</span>
          <span className="num">{fmtUSD(s.quotedTotal)}</span>
        </div>
      )}
      {!s?.hasData && <div className="jl-card-setup">Upload a quote to start tracking</div>}

      <div className="jl-card-footer">
        {s?.invoiceCount > 0 && (
          <span className="jl-cf-chip"><Icon name="file" size={10} />{s.invoiceCount} invoice{s.invoiceCount !== 1 ? 's' : ''}</span>
        )}
        {s?.lfInvoiced > 0 && <span className="jl-cf-chip">{s.lfInvoiced.toLocaleString()} LF</span>}
        {s?.flaggedCount > 0 && !isArchived && (
          <span className="jl-cf-chip jl-cf-flag"><Icon name="flag" size={10} />{s.flaggedCount} flagged</span>
        )}
        {procoreUrl && <span className="jl-cf-chip jl-cf-procore" title="Linked to Procore">&#x25CF; Procore</span>}
        {(!s || (!s.invoiceCount && !s.lfInvoiced && !s.flaggedCount)) && !procoreUrl && (
          <span className="jl-card-date">{fmtDate(job.createdAt)}</span>
        )}
        <span className="jl-cf-arrow"><Icon name="chev" size={12} /></span>
      </div>
    </div>
  );
}

// ── Job table row ─────────────────────────────────────────────────────────
function JobTableRow({ job, stats, onClick, onEdit, onArchive, onToggleActive, onDelete,
                      selectable, selected, onToggleSelect, ranged }) {
  const s = stats;
  const isArchived = job.active === false;
  const statusColor = s?.hasData ? JOB_STATUS_COLOR[s.status] : 'var(--fg-dim)';
  const pctClamped  = s ? Math.min(s.pctUsed, 1) : 0;

  return (
    <tr
      className={`jlt-row${isArchived ? ' jlt-archived' : ''}${s?.flaggedCount > 0 && !isArchived ? ' jlt-flagged' : ''}${selected ? ' jlt-selected' : ''}`}
      onClick={onClick} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {selectable && (
        <td className="jlt-check" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" className="jl-check" checked={!!selected}
            aria-label={`Select job ${job.jobNumber}`}
            onChange={() => onToggleSelect(job.id)} />
        </td>
      )}
      {/* Show the full job number exactly as stored — same as the dashboard
          cards and every job page, so the ID reads identically everywhere. */}
      <td className="jlt-num mono" title={job.jobNumber}>{job.jobNumber}</td>
      <td className="jlt-name">
        {job.jobName || <span className="jlt-empty">—</span>}
        {job.trades?.length > 0 && <TradeChips trades={job.trades} max={2} />}
      </td>
      <td className="jlt-vendor">{s?.vendor || <span className="jlt-empty">—</span>}</td>
      <td className="jlt-amt num">{s?.quotedTotal   != null ? fmtUSD(s.quotedTotal)   : '—'}</td>
      <td className="jlt-amt num">{s?.invoicedTotal != null ? fmtUSD(s.invoicedTotal) : '—'}</td>
      {/* Remaining + % Used are whole-job concepts — hidden while a date window
          is active (the matching columns drop from the header too). */}
      {!ranged && (
        <td className="jlt-amt num" style={{ color: s?.remaining < 0 ? 'var(--bad)' : undefined }}>
          {s?.remaining != null ? fmtUSD(s.remaining) : '—'}
        </td>
      )}
      {!ranged && (
        <td className="jlt-pct">
          {s?.hasData && !s?.noBill ? (
            <div className="jlt-pct-wrap">
              <div className="jlt-pct-bar">
                <div className="jlt-pct-fill" style={{ width: `${pctClamped * 100}%`, background: statusColor }} />
              </div>
              <span className="num jlt-pct-val" style={{ color: statusColor }}>
                {Math.round(s.pctUsed * 100)}%
              </span>
            </div>
          ) : <span className="jlt-empty">—</span>}
        </td>
      )}
      <td className="jlt-status">
        {isArchived ? (
          <span className="jlt-tag jlt-tag-archived">Archived</span>
        ) : s?.hasData ? (
          <span className="jlt-status-pill" style={{ '--sc': statusColor }}>
            <span className="jlt-sdot" />{jobStatusLabel(s, JOB_ROW_STATUS_LABEL)}
          </span>
        ) : <span className="jlt-empty">No data</span>}
      </td>
      <td className="jlt-flags">
        {s?.flaggedCount > 0 && !isArchived && (
          <span className="jlt-flag-chip"><Icon name="flag" size={10} />{s.flaggedCount}</span>
        )}
      </td>
      <td className="jlt-acts" onClick={(e) => e.stopPropagation()}>
        {/* UX #1 (a11y): accessible names for icon-only row actions. */}
        <button className="jl-act-btn" title="Edit" aria-label="Edit job" onClick={(e) => stopAndCall(e, onEdit)}><Icon name="pencil" size={12} /></button>
        <button className="jl-act-btn" title={isArchived ? 'Restore' : 'Archive'} aria-label={isArchived ? 'Restore job' : 'Archive job'}
          onClick={(e) => stopAndCall(e, isArchived ? onToggleActive : onArchive)}>
          <Icon name={isArchived ? 'eye' : 'archive'} size={12} />
        </button>
        <button className="jl-act-btn jl-act-del" title="Delete" aria-label="Delete job" onClick={(e) => stopAndCall(e, onDelete)}><Icon name="trash" size={12} /></button>
      </td>
    </tr>
  );
}

// ── Shared drawer styles (used by both New Job and Edit drawers) ──────────
const JobDrawerStyles = () => (
  <style>{`
    .jed-backdrop { position:fixed; inset:0; background:oklch(0 0 0 / .35); z-index:200; }
    .jed-panel {
      position:fixed; top:0; right:0; bottom:0; width:360px; z-index:201;
      background:var(--bg); border-left:1px solid var(--border); box-shadow:-8px 0 32px oklch(0 0 0 / .12);
      display:flex; flex-direction:column;
      animation: jed-in .2s cubic-bezier(.25,.46,.45,.94);
    }
    @keyframes jed-in { from { transform:translateX(100%); } to { transform:translateX(0); } }
    .jed-hd {
      display:flex; align-items:flex-start; justify-content:space-between; gap:12px;
      padding:24px 24px 20px; border-bottom:1px solid var(--border);
    }
    .jed-eyebrow { font-size:10px; letter-spacing:.14em; color:var(--fg-dim); margin-bottom:4px; }
    .jed-title   { font-size:20px; font-weight:600; letter-spacing:-0.01em; }
    .jed-close {
      appearance:none; border:1px solid var(--border); background:var(--surface-2);
      border-radius:var(--r); padding:6px; cursor:pointer; color:var(--fg-muted);
      display:grid; place-items:center; flex-shrink:0;
      transition:background .12s, color .12s;
    }
    .jed-close:hover { background:var(--surface-3); color:var(--fg); }
    .jed-body  { flex:1; overflow-y:auto; padding:20px 24px; display:flex; flex-direction:column; gap:24px; }
    .jed-section { display:flex; flex-direction:column; gap:12px; }
    .jed-section-label {
      font-size:10.5px; font-weight:600; letter-spacing:.08em; text-transform:uppercase;
      color:var(--fg-dim); padding-bottom:8px; border-bottom:1px solid var(--border);
    }
    .jed-field  { display:flex; flex-direction:column; gap:6px; }
    .jed-label  { font-size:12px; font-weight:500; color:var(--fg-muted); }
    .jed-procore-linked { display:flex; flex-direction:column; gap:10px; }
    .jed-procore-open {
      display:inline-flex; align-items:center; gap:8px;
      padding:10px 14px; border-radius:var(--r);
      background:oklch(0.62 0.12 250 / .1); border:1px solid oklch(0.62 0.12 250 / .3);
      color:oklch(0.62 0.12 250); font-size:13.5px; font-weight:500;
      text-decoration:none; transition:background .12s, border-color .12s;
    }
    .jed-procore-open:hover { background:oklch(0.62 0.12 250 / .18); border-color:oklch(0.62 0.12 250 / .5); }
    .jed-footer {
      padding:16px 24px; border-top:1px solid var(--border);
      display:flex; justify-content:flex-end; gap:8px;
    }
    .jed-err { font-size:12px; color:var(--bad); margin-top:2px; line-height:1.45; }
    .input-error { border-color:var(--bad) !important; }
  `}</style>
);

// ── Job edit drawer ───────────────────────────────────────────────────────
function JobEditDrawer({ job, onClose, onRename, onUpdateJob, onLinkProcore, onUnlinkProcore,
                         procoreProjects, procoreLoading, onLoadProcore, procoreCompanyId }) {
  const [numVal,       setNumVal]       = React.useState(job.jobNumber || '');
  const [nameVal,      setNameVal]      = React.useState(job.jobName   || '');
  const [tradesVal,    setTradesVal]    = React.useState(job.trades    || []);
  const [dirty,        setDirty]        = React.useState(false);
  const [saving,       setSaving]       = React.useState(false);
  const [showProcore,  setShowProcore]  = React.useState(false);
  const panelRef = React.useRef(null);
  useFocusTrap(panelRef); // UX #5: focus trap + restore for the edit drawer

  React.useEffect(() => {
    setNumVal(job.jobNumber || '');
    setNameVal(job.jobName  || '');
    setTradesVal(job.trades || []);
    setDirty(false);
  }, [job.jobNumber, job.jobName, job.id]);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const procoreUrl = job.procoreUrl ||
    (job.procoreProjectId && procoreCompanyId
      ? `https://sandbox.procore.com/webclients/host/companies/${procoreCompanyId}/projects/${job.procoreProjectId}/tools/projecthome`
      : null);

  const handleSave = async () => {
    if (!numVal.trim()) return;
    if (!dirty) { onClose(); return; }
    setSaving(true);
    try {
      if (numVal.trim() !== job.jobNumber) await onRename(job, 'number', numVal.trim());
      const newName = nameVal.trim();
      if (newName !== (job.jobName || '')) await onRename(job, 'name', newName);
      if (onUpdateJob && JSON.stringify(tradesVal) !== JSON.stringify(job.trades || [])) {
        await onUpdateJob(job, { trades: tradesVal });
      }
    } finally {
      setSaving(false);
      onClose();
    }
  };

  const handleOpenProcore = async () => {
    setShowProcore(true);
    await onLoadProcore();
  };

  return (
    <>
      <div className="jed-backdrop" role="presentation" onClick={onClose} />
      <div className="jed-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label={`Edit job ${job.jobNumber}`}>
        <div className="jed-hd">
          <div>
            <div className="jed-eyebrow mono">JOB</div>
            <div className="jed-title mono">{job.jobNumber}</div>
          </div>
          <button className="jed-close" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className="jed-body">
          <div className="jed-section">
            <div className="jed-section-label">Details</div>
            <div className="jed-field">
              <label className="jed-label" htmlFor="jed-edit-num">Job Number</label>
              <input id="jed-edit-num" className="input" value={numVal} onChange={(e) => { setNumVal(e.target.value); setDirty(true); }} />
            </div>
            <div className="jed-field">
              <label className="jed-label" htmlFor="jed-edit-name">Job Name</label>
              <input id="jed-edit-name" className="input" value={nameVal} placeholder="Optional…" onChange={(e) => { setNameVal(e.target.value); setDirty(true); }} />
            </div>
          </div>

          <div className="jed-section">
            <div className="jed-section-label">Trades / categories</div>
            <TradePicker value={tradesVal} onChange={(v) => { setTradesVal(v); setDirty(true); }} />
          </div>

          <div className="jed-section">
            <div className="jed-section-label">Procore</div>
            {job.procoreProjectId ? (
              <div className="jed-procore-linked">
                {procoreUrl && (
                  <a className="jed-procore-open" href={safeUrl(procoreUrl)} target="_blank" rel="noopener noreferrer"> {/* audit #7 */}
                    <Icon name="external" size={14} />
                    Open project in Procore
                  </a>
                )}
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--bad)' }} onClick={() => onUnlinkProcore(job)}>
                  Unlink Procore
                </button>
              </div>
            ) : !showProcore ? (
              <button className="btn btn-ghost btn-sm" onClick={handleOpenProcore}>
                Link to a Procore project
              </button>
            ) : (
              <select className="input" defaultValue="" disabled={procoreLoading} aria-label="Procore project"
                onChange={(e) => {
                  const proj = (procoreProjects || []).find((p) => p.id === +e.target.value);
                  if (proj) onLinkProcore(job, proj);
                }}>
                <option value="">{procoreLoading ? 'Loading…' : 'Select a project…'}</option>
                {(procoreProjects || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.project_number ? `${p.project_number} — ` : ''}{p.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="jed-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !numVal.trim()}>
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Done'}
          </button>
        </div>
      </div>
      <JobDrawerStyles />
    </>
  );
}


// ── New job drawer ────────────────────────────────────────────────────────
function NewJobDrawer({ onClose, onCreate, existingNumbers, procoreProjects, procoreLoading, onLoadProcore, procoreCompanyId }) {
  const [numVal,          setNumVal]          = React.useState('');
  const [nameVal,         setNameVal]         = React.useState('');
  const [tradesVal,       setTradesVal]       = React.useState([]);
  const [creating,        setCreating]        = React.useState(false);
  const [showProcore,     setShowProcore]     = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState(null);
  const numRef = React.useRef(null);
  const panelRef = React.useRef(null);
  useFocusTrap(panelRef); // UX #5: focus trap + restore for the new-job drawer

  React.useEffect(() => { numRef.current?.focus(); }, []);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Live duplicate check — a job number must be unique (case-insensitive).
  const trimmedNum = numVal.trim();
  const isDup = trimmedNum.length > 0 && !!existingNumbers && existingNumbers.has(trimmedNum.toLowerCase());

  const handleCreate = async () => {
    const num = numVal.trim();
    if (!num || creating || isDup) return;
    setCreating(true);
    try {
      await onCreate({
        jobNumber: num,
        jobName: nameVal.trim() || undefined,
        trades: tradesVal,
        procoreProjectId: selectedProject?.id,
        procoreUrl: selectedProject && procoreCompanyId
          ? `https://sandbox.procore.com/webclients/host/companies/${procoreCompanyId}/projects/${selectedProject.id}/tools/projecthome`
          : undefined,
      });
    } catch {
      setCreating(false);
    }
  };

  const handleLoadProcore = async () => {
    setShowProcore(true);
    await onLoadProcore();
  };

  return (
    <>
      <div className="jed-backdrop" role="presentation" onClick={onClose} />
      <div className="jed-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label="Create job">
        <div className="jed-hd">
          <div>
            <div className="jed-eyebrow mono">NEW JOB</div>
            <div className="jed-title">Create Job</div>
          </div>
          <button className="jed-close" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className="jed-body">
          <div className="jed-section">
            <div className="jed-section-label">Details</div>
            <div className="jed-field">
              <label className="jed-label" htmlFor="jed-new-num">Job Number <span style={{ color: 'var(--bad)' }}>*</span></label>
              <input id="jed-new-num" ref={numRef} className={`input${isDup ? ' input-error' : ''}`} value={numVal} placeholder="e.g. 2024-001"
                aria-invalid={isDup}
                onChange={(e) => setNumVal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
              {isDup && (
                <div className="jed-err">A job numbered “{trimmedNum}” already exists. Pick a different number.</div>
              )}
            </div>
            <div className="jed-field">
              <label className="jed-label" htmlFor="jed-new-name">Job Name</label>
              <input id="jed-new-name" className="input" value={nameVal} placeholder="Optional…"
                onChange={(e) => setNameVal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
            </div>
          </div>

          <div className="jed-section">
            <div className="jed-section-label">Trades / categories</div>
            <TradePicker value={tradesVal} onChange={setTradesVal} />
          </div>

          <div className="jed-section">
            <div className="jed-section-label">Procore</div>
            {!showProcore ? (
              <button className="btn btn-ghost btn-sm" onClick={handleLoadProcore}>
                Import from Procore
              </button>
            ) : (
              <div className="jed-field">
                <select className="input" value={selectedProject?.id || ''} disabled={procoreLoading} aria-label="Procore project"
                  onChange={(e) => {
                    const proj = (procoreProjects || []).find((p) => p.id === +e.target.value);
                    setSelectedProject(proj || null);
                    if (proj) {
                      setNumVal(proj.project_number || String(proj.id));
                      setNameVal(proj.name || '');
                    }
                  }}>
                  <option value="">{procoreLoading ? 'Loading…' : 'Select a project…'}</option>
                  {(procoreProjects || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_number ? `${p.project_number} — ` : ''}{p.name}
                    </option>
                  ))}
                </select>
                {selectedProject && (
                  <div style={{ fontSize: '12px', color: 'var(--ok)', marginTop: '4px' }}>
                    ✓ Will link to: {selectedProject.name}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="jed-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating || !numVal.trim() || isDup}>
            {creating ? 'Creating…' : 'Create Job'}
          </button>
        </div>
      </div>
      <JobDrawerStyles />
    </>
  );
}

// ── Delete confirmation modal ─────────────────────────────────────────────
function DeleteConfirmModal({ job, onConfirm, onCancel }) {
  const dialogRef = React.useRef(null);
  useFocusTrap(dialogRef); // focus lands on Cancel, not on the page behind
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="dcm-overlay" role="presentation" onClick={onCancel}>
      <div className="dcm-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Delete this job?" onClick={(e) => e.stopPropagation()}>
        <div className="dcm-icon-wrap">
          <Icon name="trash" size={22} />
        </div>
        <div className="dcm-title">Delete this job?</div>
        <div className="dcm-body">
          <span className="mono dcm-num">{job.jobNumber}</span>
          {job.jobName && <span className="dcm-name"> · {job.jobName}</span>}
          <span> will be permanently deleted and cannot be recovered.</span>
        </div>
        <div className="dcm-actions">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="dcm-del-btn" onClick={onConfirm}>Delete job</button>
        </div>
      </div>
    </div>
  );
}

// ── Archive confirmation modal ────────────────────────────────────────────
function ArchiveConfirmModal({ job, onConfirm, onCancel }) {
  const dialogRef = React.useRef(null);
  useFocusTrap(dialogRef);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="dcm-overlay" role="presentation" onClick={onCancel}>
      <div className="dcm-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Archive this job?" onClick={(e) => e.stopPropagation()}>
        <div className="dcm-icon-wrap dcm-icon-warn">
          <Icon name="archive" size={22} />
        </div>
        <div className="dcm-title">Archive this job?</div>
        <div className="dcm-body">
          <span className="mono dcm-num">{job.jobNumber}</span>
          {job.jobName && <span className="dcm-name"> · {job.jobName}</span>}
          <span> will be moved to the archive. You can restore it at any time.</span>
        </div>
        <div className="dcm-actions">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="dcm-archive-btn" onClick={onConfirm}>Archive job</button>
        </div>
      </div>
    </div>
  );
}

// ── Date-range picker ─────────────────────────────────────────────────────
// Header control shared by the Dashboard and Jobs views. A preset dropdown
// (All time / Last 30 / Last 90 / This month / This quarter / This year) plus a
// Custom mode that reveals two native date inputs. Self-styled so it travels
// with either screen. Selecting a window scopes the spend metrics (see the
// hide-% behavior in JobListView); the quote baseline is never date-bound.
function DateRangePicker({ rangeId, customFrom, customTo, presets, isRanged, onPreset, onCustomFrom, onCustomTo }) {
  return (
    <div className="jl-range">
      <Icon name="history" size={13} className="jl-range-icon" />
      <select className="jl-range-select" value={rangeId} aria-label="Filter by date range"
        onChange={(e) => onPreset(e.target.value)}>
        {presets.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        <option value="custom">Custom…</option>
      </select>
      {rangeId === 'custom' && (
        <span className="jl-range-custom">
          <input type="date" className="jl-range-date" value={customFrom} max={customTo || undefined}
            aria-label="From date" onChange={(e) => onCustomFrom(e.target.value)} />
          <span className="jl-range-dash">–</span>
          <input type="date" className="jl-range-date" value={customTo} min={customFrom || undefined}
            aria-label="To date" onChange={(e) => onCustomTo(e.target.value)} />
        </span>
      )}
      {isRanged && (
        <button className="jl-range-clear" title="Clear date range" aria-label="Clear date range"
          onClick={() => onPreset('all')}>
          <Icon name="x" size={12} />
        </button>
      )}
    </div>
  );
}

// ── Recent activity feed ──────────────────────────────────────────────────
// Dashboard-only. Events are DERIVED from existing job/invoice data (there's no
// audit log): each invoice's _addedAt → "invoice added", each job's createdAt →
// "job created". Rows deep-link into the job (invoice → its Invoices tab). The
// feed honors the active date range like every other spend surface.
function ActivityFeed({ items, onOpenInvoice, onOpenJob }) {
  if (!items.length) return null;
  return (
    <div className="jl-activity">
      <div className="jl-section-hd">
        <Icon name="history" size={13} style={{ color: 'var(--accent)' }} />
        <span className="jl-section-title">Recent activity</span>
      </div>
      <div className="jl-act-feed">
        {items.map((ev) => (
          <button key={ev.key} className="jl-actv-row"
            onClick={() => (ev.kind === 'invoice' ? (onOpenInvoice || onOpenJob)(ev.job, ev.invId) : onOpenJob(ev.job))}>
            <span className={`jl-actv-icon jl-actv-${ev.kind}`}>
              <Icon name={ev.kind === 'invoice' ? 'file' : 'plus'} size={13} />
            </span>
            <span className="jl-actv-text">
              {ev.kind === 'invoice' ? (
                <>
                  <strong>Invoice {ev.invId || '—'}</strong> added to <span className="mono">{ev.jobNumber}</span>
                  {ev.vendor && <span className="jl-actv-meta"> · {ev.vendor}</span>}
                </>
              ) : (
                <><strong>Job <span className="mono">{ev.jobNumber}</span></strong> created</>
              )}
            </span>
            {ev.kind === 'invoice' && ev.total != null && (
              <span className="num jl-actv-amt">{fmtUSD(ev.total)}</span>
            )}
            <span className="jl-actv-time">{relativeTime(ev.at)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── JobListView (dashboard root) ──────────────────────────────────────────
const JL_PAGE_SIZE = 8;

function JobListView({ user, onSelectJob, onNav, vendorRules, mode = 'jobs', onOpenJobInvoice }) {
  const isDashboard = mode === 'dashboard';
  // Jobs come from the shared session-wide store (invodiff-data.jsx) — ONE
  // Firestore subscription serves this view, the dashboard, and Price History,
  // and split-shape jobs arrive with their invoices already joined in.
  const { jobs: storeJobs, loading, permError } = useJobsStore();
  const jobs = storeJobs || NO_JOBS;
  const [showNewJobDrawer, setShowNewJobDrawer] = React.useState(false);
  const [showArchived, setShowArchived] = React.useState(false);
  const [query, setQuery]           = React.useState('');
  const [activeExpanded, setActiveExpanded] = React.useState(false);
  const [archivedExpanded, setArchivedExpanded] = React.useState(false);
  const [pendingDelete,   setPendingDelete]   = React.useState(null);
  const [pendingArchive,  setPendingArchive]  = React.useState(null);
  const searchRef  = React.useRef(null);
  const { toasts, addToast } = useToast();

  const [procoreProjects,  setProcoreProjects]  = React.useState(null);
  const [procoreLoading,   setProcoreLoading]   = React.useState(false);
  const [procoreCompanyId, setProcoreCompanyId] = React.useState(null);
  const [drawerJobId,           setDrawerJobId]           = React.useState(null);
  // View mode + sort persist across reloads (per browser) so a PM's preferred
  // layout sticks. flaggedOnly is a transient filter (driven by the metric card).
  const [viewMode,              setViewMode]              = React.useState(() => readPref('view', 'grid')); // 'grid' | 'table'
  const [sortBy,                setSortBy]                = React.useState(() => readPref('sort', 'recent')); // recent|number|usage|flags|remaining
  const [flaggedOnly,           setFlaggedOnly]           = React.useState(false);
  const [tradeFilter,           setTradeFilter]           = React.useState(null); // null = all trades
  // Bulk selection (table view only). A single Set of job ids spans the active
  // and archived tables; the action bar adapts to what's selected. confirmCfg
  // drives the shared ConfirmModal for the destructive bulk actions.
  const [selected,   setSelected]   = React.useState(() => new Set());
  const [confirmCfg, setConfirmCfg] = React.useState(null);
  // Date-range filter — persists per browser like view/sort. Preset id, plus the
  // two endpoints for 'custom' (YYYY-MM-DD strings).
  const [rangeId,    setRangeId]    = React.useState(() => readPref('range', 'all'));
  const [customFrom, setCustomFrom] = React.useState(() => readPref('rangeFrom', ''));
  const [customTo,   setCustomTo]   = React.useState(() => readPref('rangeTo', ''));

  React.useEffect(() => { writePref('view', viewMode); }, [viewMode]);
  React.useEffect(() => { writePref('sort', sortBy);   }, [sortBy]);
  React.useEffect(() => { writePref('range', rangeId);       }, [rangeId]);
  React.useEffect(() => { writePref('rangeFrom', customFrom); }, [customFrom]);
  React.useEffect(() => { writePref('rangeTo', customTo);     }, [customTo]);
  // Checkboxes only exist in the table layout, so drop the selection when the
  // user switches to the card grid — otherwise a hidden selection lingers.
  React.useEffect(() => { setSelected(new Set()); }, [viewMode]);

  // Resolve the active window. Presets are computed once per mount (their bounds
  // are relative to "now"); 'custom' parses the two date inputs (to = end of day
  // so the upper bound is inclusive). isRanged drives the hide-% behavior below.
  const presets = React.useMemo(() => dateRangePresets(), []);
  const activeRange = React.useMemo(() => {
    if (rangeId === 'custom') {
      const from = customFrom ? new Date(`${customFrom}T00:00:00`)     : null;
      const to   = customTo   ? new Date(`${customTo}T23:59:59.999`)   : null;
      if (!from && !to) return { id: 'all', from: null, to: null, label: 'All time' };
      const fmt = (d) => d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '…';
      return { id: 'custom', from, to, label: `${fmt(from)} – ${fmt(to)}` };
    }
    return presets.find((p) => p.id === rangeId) || presets[0];
  }, [rangeId, customFrom, customTo, presets]);
  const isRanged = activeRange.id !== 'all' && !!(activeRange.from || activeRange.to);

  const migratedRef = React.useRef(false);

  // One-time migration: copy jobs from the old per-user path to the shared /jobs collection.
  // Runs after the initial load when /jobs/ is empty — only the original job owner can
  // read the old path, so other users' attempts are silent no-ops.
  React.useEffect(() => {
    if (loading || jobs.length > 0 || migratedRef.current) return;
    migratedRef.current = true;
    fbDb.collection('users').doc(user.uid).collection('jobs').get()
      .then(async (snap) => {
        if (snap.empty) return;
        const batch = fbDb.batch();
        snap.docs.forEach((d) => batch.set(fbDb.collection('jobs').doc(d.id), d.data()));
        await batch.commit();
        addToast(`Migrated ${snap.docs.length} job${snap.docs.length !== 1 ? 's' : ''} to shared pool`);
      })
      .catch(() => {});
  }, [loading, jobs.length, user.uid]);

  const handleCreate = async ({ jobNumber, jobName, trades, procoreProjectId, procoreUrl }) => {
    // Guard against duplicate job numbers (case-insensitive) — the drawer also
    // blocks this inline, but enforce it here too so no path can create one.
    const norm = (jobNumber || '').trim().toLowerCase();
    if (jobs.some((j) => (j.jobNumber || '').trim().toLowerCase() === norm)) {
      addToast(`Job number "${jobNumber}" already exists`, 'warn');
      throw new Error('duplicate-job-number');
    }
    // New jobs are born on the split shape (invoicesInSub): their invoices live
    // in the jobs/{id}/invoices subcollection from day one. The empty legacy
    // array is kept only so old readers see a consistent field.
    const doc = { jobNumber, createdAt: firebase.firestore.FieldValue.serverTimestamp(), quote: null, invoices: [], invoicesInSub: true, active: true };
    if (jobName)         doc.jobName         = jobName;
    if (trades && trades.length) doc.trades   = trades;
    if (procoreProjectId) doc.procoreProjectId = procoreProjectId;
    if (procoreUrl)       doc.procoreUrl       = procoreUrl;
    const ref  = await fbDb.collection('jobs').add(doc);
    const snap = await ref.get();
    addToast(`Job "${jobNumber}" created`);
    setShowNewJobDrawer(false);
    onSelectJob({ id: snap.id, ...snap.data() });
  };

  const handleToggleActive = async (job) => {
    const restoring = job.active === false;
    try {
      await fbDb.collection('jobs').doc(job.id)
        .update({ active: restoring ? true : false });
      addToast(restoring ? `Job "${job.jobNumber}" restored` : `Job "${job.jobNumber}" archived`);
    } catch (e) {
      console.error('Failed to update job:', e);
    }
  };

  const handleArchive = (job) => setPendingArchive(job);

  const handleConfirmArchive = async () => {
    const job = pendingArchive;
    setPendingArchive(null);
    await handleToggleActive(job);
  };

  const handleDelete = (job) => setPendingDelete(job);

  const handleConfirmDelete = async () => {
    const job = pendingDelete;
    setPendingDelete(null);
    try {
      // deleteJobDeep also purges the invoices subcollection — a bare
      // doc.delete() would orphan every invoice doc on a split-shape job.
      await deleteJobDeep(job.id);
      addToast(`Job "${job.jobNumber}" deleted`, 'warn');
    } catch (e) {
      console.error('Failed to delete job:', e);
    }
  };

  const handleRename = async (job, field, value) => {
    try {
      const update = {};
      if (field === 'number') {
        if (!value) return;
        const norm = value.trim().toLowerCase();
        if (jobs.some((j) => j.id !== job.id && (j.jobNumber || '').trim().toLowerCase() === norm)) {
          addToast(`Job number "${value}" already exists`, 'warn');
          throw new Error('duplicate-job-number');
        }
        update.jobNumber = value;
      } else {
        update.jobName = value || firebase.firestore.FieldValue.delete();
      }
      await fbDb.collection('jobs').doc(job.id).update(update);
      addToast(field === 'number' ? `Job number updated to "${value}"` : value ? `Job name updated` : `Job name removed`);
    } catch (e) {
      console.error('Failed to rename job:', e);
    }
  };

  const fetchProcoreProjects = async () => {
    if (procoreProjects !== null) return;
    setProcoreLoading(true);
    try {
      // The /api/procore proxy now requires a Firebase ID token (audit #5).
      const u = window.fbAuth?.currentUser;
      const authHeaders = u ? { Authorization: `Bearer ${await u.getIdToken()}` } : {};
      const [projRes, metaRes] = await Promise.all([
        fetch('/api/procore?path=/rest/v1.0/projects', { headers: authHeaders }),
        fetch('/api/procore?path=/_meta', { headers: authHeaders }),
      ]);
      const [projects, meta] = await Promise.all([projRes.json(), metaRes.json()]);
      setProcoreProjects(Array.isArray(projects) ? projects : []);
      if (meta.companyId) setProcoreCompanyId(meta.companyId);
    } catch {
      setProcoreProjects([]);
    } finally {
      setProcoreLoading(false);
    }
  };



  const handleLinkProcore = async (job, project) => {
    const url = procoreCompanyId
      ? `https://sandbox.procore.com/webclients/host/companies/${procoreCompanyId}/projects/${project.id}/tools/projecthome`
      : null;
    try {
      const update = { procoreProjectId: project.id };
      if (url) update.procoreUrl = url;
      await fbDb.collection('jobs').doc(job.id).update(update);
      addToast(`Linked to Procore: ${project.project_number || project.name}`);
    } catch (e) {
      console.error('Failed to link Procore project:', e);
    }
  };

  const handleUnlinkProcore = async (job) => {
    try {
      await fbDb.collection('jobs').doc(job.id).update({
        procoreProjectId: firebase.firestore.FieldValue.delete(),
        procoreUrl:       firebase.firestore.FieldValue.delete(),
      });
      addToast('Procore link removed');
    } catch (e) {
      console.error('Failed to unlink Procore:', e);
    }
  };

  // Per-job rollups. When a window is active, the job's invoices are filtered to
  // the range BEFORE the stats engine runs, so invoiced/footage/flag counts all
  // reflect the window; the quote baseline is undated and passes through whole.
  // Memoized per job in the session-wide cache (jobStatsFor, above): the jobs
  // store keeps object identities stable for untouched jobs across snapshots,
  // so editing one job re-runs the matcher for that job only — not the whole
  // portfolio — and switching Dashboard ↔ Jobs re-runs it for none.
  const jobStats = React.useMemo(() => {
    const range = isRanged ? activeRange : null;
    const out = {};
    for (const j of jobs) out[j.id] = jobStatsFor(j, vendorRules, range);
    return out;
  }, [jobs, vendorRules, isRanged, activeRange.from, activeRange.to]);
  // Cache write happens after commit, not during render (render must stay
  // pure — React can discard/replay it). One WeakMap pass, not a matcher re-run.
  React.useEffect(() => {
    const range = isRanged ? activeRange : null;
    for (const j of jobs) rememberJobStats(j, vendorRules, range, jobStats[j.id]);
  }, [jobStats, jobs, vendorRules, isRanged, activeRange.from, activeRange.to]);

  const activeJobs   = React.useMemo(() => jobs.filter((j) => j.active !== false), [jobs]);
  const archivedJobs = React.useMemo(() => jobs.filter((j) => j.active === false),  [jobs]);

  // All existing job numbers (active + archived), lowercased — used to block
  // creating/renaming to a duplicate.
  const existingNumbers = React.useMemo(
    () => new Set(jobs.flatMap((j) => {
      const n = (j.jobNumber || '').trim().toLowerCase();
      return n ? [n] : [];
    })),
    [jobs]
  );

  const isSearching = query.trim().length > 0;

  const matchesQuery = React.useCallback((job) => {
    const q = query.toLowerCase();
    return (
      job.jobNumber?.toLowerCase().includes(q) ||
      (job.jobName  || '').toLowerCase().includes(q) ||
      (jobStats[job.id]?.vendor || '').toLowerCase().includes(q)
    );
  }, [query, jobStats]);

  // Every distinct trade tag across all jobs, sorted — drives the filter chips.
  const allTrades = React.useMemo(
    () => Array.from(new Set(jobs.flatMap((j) => j.trades || []))).sort((a, b) => a.localeCompare(b)),
    [jobs]
  );

  const filteredActive   = React.useMemo(() => {
    let list = isSearching ? activeJobs.filter(matchesQuery) : activeJobs;
    if (flaggedOnly) list = list.filter((j) => (jobStats[j.id]?.flaggedCount || 0) > 0);
    if (tradeFilter) list = list.filter((j) => (j.trades || []).includes(tradeFilter));
    return list;
  }, [activeJobs, isSearching, matchesQuery, flaggedOnly, tradeFilter, jobStats]);

  const filteredArchived = React.useMemo(() =>
    isSearching ? archivedJobs.filter(matchesQuery) : archivedJobs,
  [archivedJobs, isSearching, matchesQuery]);

  // Client-side sort over the active list. 'recent' keeps Firestore's createdAt-desc
  // order; the rest re-rank by the per-job stats so a PM can triage by spend or risk.
  const sortedActive = React.useMemo(
    () => sortJobs(filteredActive, sortBy, jobStats),
    [filteredActive, sortBy, jobStats]
  );

  const visibleActive   = (activeExpanded   || isSearching) ? sortedActive     : sortedActive.slice(0, JL_PAGE_SIZE);
  const visibleArchived = (archivedExpanded || isSearching) ? filteredArchived : filteredArchived.slice(0, JL_PAGE_SIZE);
  const moreActive   = filteredActive.length   - JL_PAGE_SIZE;
  const moreArchived = filteredArchived.length - JL_PAGE_SIZE;

  const [approvalMetrics, setApprovalMetrics] = React.useState({ pending: 0, exposure: 0 });

  React.useEffect(() => {
    if (!user?.uid || !window.APPROVALS_ENABLED) return; // approval workflow hidden for now
    const unsub = fbDb
      .collection('users').doc(user.uid)
      .collection('approvalInvoices')
      .where('status', 'in', ['PENDING_REVIEW', 'DISPUTED', 'APPROVED_L1', 'APPROVED_L2', 'APPROVED_L3'])
      .onSnapshot((snap) => {
        const docs = snap.docs.map((d) => d.data());
        setApprovalMetrics({
          pending:  docs.length,
          exposure: +docs.reduce((s, d) => s + (d.totalCents || 0) / 100, 0).toFixed(2),
        });
      }, () => {});
    return unsub;
  }, [user?.uid]);

  const metrics = React.useMemo(
    () => computePortfolioMetrics(activeJobs, jobStats),
    [jobStats, activeJobs]
  );

  const priorityJobs = React.useMemo(
    () => activeJobs
      .filter((j) => jobStats[j.id]?.flaggedCount > 0)
      .sort((a, b) => (jobStats[b.id]?.flaggedCount || 0) - (jobStats[a.id]?.flaggedCount || 0))
      .slice(0, 4),
    [activeJobs, jobStats]
  );

  // Recent activity — derived from active jobs: each invoice's _addedAt and each
  // job's createdAt become an event, newest first, top 8. Honors the date window
  // (invoice events by their doc/added date, job events by createdAt).
  const activity = React.useMemo(() => {
    const events = [];
    for (const job of activeJobs) {
      if (job.createdAt) {
        events.push({ kind: 'job', job, jobId: job.id, jobNumber: job.jobNumber, at: job.createdAt });
      }
      for (const inv of (job.invoices || [])) {
        if (inv._hidden) continue;
        events.push({
          kind: 'invoice', job, jobId: job.id, jobNumber: job.jobNumber,
          invId: inv.id, vendor: inv.vendor, total: statedInvoiceTotal(inv),
          at: inv._addedAt || inv.date, _inv: inv,
        });
      }
    }
    const inRange = (ev) => {
      if (!isRanged) return true;
      if (ev.kind === 'invoice') return invoiceInRange(ev._inv, activeRange);
      const d = ev.at?.toDate ? ev.at.toDate() : (ev.at ? new Date(ev.at) : null);
      if (!d || isNaN(d.getTime())) return false;
      if (activeRange.from && d < activeRange.from) return false;
      if (activeRange.to   && d > activeRange.to)   return false;
      return true;
    };
    return events
      .filter(inRange)
      .map((ev) => ({ ...ev, _ms: tsMillis(ev.at) }))
      .sort((a, b) => b._ms - a._ms)
      .slice(0, 8)
      .map((ev) => ({ ...ev, key: `${ev.kind}:${ev.jobId}:${ev.invId || ''}:${ev._ms}` }));
  }, [activeJobs, isRanged, activeRange.from, activeRange.to]);

  const drawerJob = drawerJobId ? (jobs.find((j) => j.id === drawerJobId) || null) : null;

  // Build + download a one-row-per-job CSV for an arbitrary job list. Mirrors the
  // table columns + a few extras; numbers are raw (no $/commas) so they drop
  // straight into a spreadsheet. A leading BOM helps Excel detect UTF-8.
  const exportJobsCSV = (list, namePart = 'jobs') => {
    if (!list.length) { addToast('No jobs to export', 'warn'); return; }
    const headers = JOB_CSV_HEADERS;
    const rows = list.map((job) => jobCSVRow(job, jobStats[job.id], isRanged));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadFile(`kcg-${namePart}_${stamp}.csv`, '﻿' + toCSV(headers, rows), 'text/csv');
    addToast(`Exported ${rows.length} job${rows.length !== 1 ? 's' : ''} to CSV`);
  };
  // Toolbar export covers the current filtered + sorted active list.
  const handleExport = () => exportJobsCSV(sortedActive);

  // ── Bulk selection ────────────────────────────────────────────────────────
  const toggleSelect = React.useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  // Toggle a whole table's worth of ids: if every id is already selected, clear
  // them; otherwise add them all (matches the header checkbox's on/off semantics).
  const toggleSelectAll = React.useCallback((ids) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = ids.length > 0 && ids.every((id) => next.has(id));
      ids.forEach((id) => { if (allOn) next.delete(id); else next.add(id); });
      return next;
    });
  }, []);
  const clearSelection = () => setSelected(new Set());

  const selectedJobs = React.useMemo(() => jobs.filter((j) => selected.has(j.id)), [jobs, selected]);
  const selActive    = selectedJobs.filter((j) => j.active !== false);
  const selArchived  = selectedJobs.filter((j) => j.active === false);

  // Apply a Firestore batch over the selection, then clear it. One commit keeps
  // the multi-job change atomic and within the 500-write batch limit.
  const runBulk = async (apply, okMsg, tone) => {
    try {
      const batch = fbDb.batch();
      apply(batch);
      await batch.commit();
      clearSelection();
      addToast(okMsg, tone);
    } catch (e) {
      console.error('Bulk action failed:', e);
      addToast('Bulk action failed', 'warn');
    }
  };
  const plural = (n) => (n !== 1 ? 's' : '');

  const handleBulkArchive = () => {
    const list = selActive;
    if (!list.length) return;
    setConfirmCfg({
      title: `Archive ${list.length} job${plural(list.length)}?`,
      body: <>The selected job{plural(list.length)} will be moved to the archive. You can restore {list.length !== 1 ? 'them' : 'it'} at any time.</>,
      confirmLabel: 'Archive', tone: 'warn', icon: 'archive',
      onConfirm: () => runBulk(
        (b) => list.forEach((j) => b.update(fbDb.collection('jobs').doc(j.id), { active: false })),
        `${list.length} job${plural(list.length)} archived`),
    });
  };
  const handleBulkRestore = () => {
    const list = selArchived;
    if (!list.length) return;
    // Restoring is non-destructive — apply directly, no confirmation.
    runBulk(
      (b) => list.forEach((j) => b.update(fbDb.collection('jobs').doc(j.id), { active: true })),
      `${list.length} job${plural(list.length)} restored`);
  };
  const handleBulkDelete = () => {
    const list = selectedJobs;
    if (!list.length) return;
    setConfirmCfg({
      title: `Delete ${list.length} job${plural(list.length)}?`,
      body: <>The selected job{plural(list.length)} will be permanently deleted and cannot be recovered.</>,
      confirmLabel: 'Delete', tone: 'bad', icon: 'trash',
      // Not a single batch: each job's invoices subcollection must be purged
      // with it (deleteJobDeep), which needs its own reads + chunked batches.
      onConfirm: async () => {
        try {
          for (const j of list) await deleteJobDeep(j.id);
          clearSelection();
          addToast(`${list.length} job${plural(list.length)} deleted`, 'warn');
        } catch (e) {
          console.error('Bulk delete failed:', e);
          addToast('Bulk delete failed — some jobs may remain', 'warn');
        }
      },
    });
  };
  const handleBulkExport = () => exportJobsCSV(selectedJobs, 'jobs-selected');

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  // "/" focuses search (Jobs page), "n" opens New Job, Esc clears a selection.
  // Suppressed while typing in a field or while any drawer/modal is open, so it
  // never fights those surfaces' own key handling.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' ||
        el.tagName === 'SELECT' || el.isContentEditable);
      const overlayOpen = showNewJobDrawer || !!drawerJobId || !!pendingDelete ||
        !!pendingArchive || !!confirmCfg;
      if (overlayOpen) return;
      if (e.key === '/' && !typing && searchRef.current) {
        e.preventDefault(); searchRef.current.focus();
      } else if ((e.key === 'n' || e.key === 'N') && !typing) {
        e.preventDefault(); setShowNewJobDrawer(true);
      } else if (e.key === 'Escape' && !typing && selected.size > 0) {
        setSelected(new Set());
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showNewJobDrawer, drawerJobId, pendingDelete, pendingArchive, confirmCfg, selected.size]);

  return (
    <>
    {showNewJobDrawer && (
      <NewJobDrawer
        onClose={() => setShowNewJobDrawer(false)}
        onCreate={handleCreate}
        existingNumbers={existingNumbers}
        procoreProjects={procoreProjects}
        procoreLoading={procoreLoading}
        onLoadProcore={fetchProcoreProjects}
        procoreCompanyId={procoreCompanyId}
      />
    )}
    {drawerJob && (
      <JobEditDrawer
        job={drawerJob}
        onClose={() => setDrawerJobId(null)}
        onRename={handleRename}
        onUpdateJob={updateJobFields}
        onLinkProcore={handleLinkProcore}
        onUnlinkProcore={handleUnlinkProcore}
        procoreProjects={procoreProjects}
        procoreLoading={procoreLoading}
        onLoadProcore={fetchProcoreProjects}
        procoreCompanyId={procoreCompanyId}
      />
    )}
    <div className="jl-view">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="jl-hd">
        <div>
          <div className="jl-eyebrow">{isDashboard ? 'Dashboard' : 'Jobs'}</div>
          {isDashboard ? (
            <>
              <h1 className="jl-welcome">
                Welcome back, <strong>{user?.displayName || user?.email?.split('@')[0] || ''}</strong>
              </h1>
              <div className="jl-welcome-sub">Here's an overview of your active jobs.</div>
            </>
          ) : (
            <>
              <h1 className="jl-welcome">All jobs</h1>
              <div className="jl-welcome-sub">Browse, search, and manage every job.</div>
            </>
          )}
        </div>
        <div className="jl-hd-actions">
          <DateRangePicker
            rangeId={rangeId} customFrom={customFrom} customTo={customTo}
            presets={presets} isRanged={isRanged}
            onPreset={setRangeId} onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
          />
          <button className="btn btn-primary" onClick={() => setShowNewJobDrawer(true)}>
            <Icon name="plus" size={14} /> New Job
          </button>
        </div>
      </div>

      {/* ── Active date-range banner (explains the hidden %/remaining) ── */}
      {isRanged && (
        <div className="jl-range-note">
          <Icon name="history" size={13} />
          <span>Spend figures limited to <strong>{activeRange.label}</strong> — budget % and remaining are hidden for this window.</span>
          <button className="jl-filter-clear" onClick={() => setRangeId('all')}>Clear</button>
        </div>
      )}

      {/* ── Summary metrics (Dashboard only) ────────────────── */}
      {isDashboard && !loading && activeJobs.length > 0 && (
        <div className="jl-metrics">
          <DashMetric
            icon="file"   label="Active jobs"
            value={metrics.totalJobs}
            sub={`${metrics.invoiceCount} invoice${metrics.invoiceCount !== 1 ? 's' : ''} processed`}
          />
          <DashMetric
            icon="delta"  label="Budget tracked"
            value={fmtUSD(metrics.budgetTotal)}
            sub={`${fmtUSD(metrics.invoicedTotal)} invoiced so far`}
            tone="info"
          />
          <DashMetric
            icon="arrow"  label="Footage invoiced"
            value={metrics.lfTracked > 0 ? metrics.lfTracked.toLocaleString() + ' LF' : '—'}
            sub={metrics.lfTracked > 0 ? 'Linear feet across all jobs' : 'No LF items yet'}
            tone={metrics.lfTracked > 0 ? 'info' : 'default'}
          />
          <DashMetric
            icon="flag"   label="Need review"
            value={metrics.needsReview}
            sub={metrics.needsReview === 0 ? 'All jobs on track' : `${metrics.needsReview} job${metrics.needsReview !== 1 ? 's' : ''} with issues`}
            tone={metrics.needsReview > 0 ? 'warn' : 'ok'}
            onClick={metrics.needsReview > 0 ? () => setFlaggedOnly((v) => !v) : undefined}
            active={flaggedOnly}
            cta={flaggedOnly ? 'Clear filter' : 'Filter jobs'}
          />
          {window.APPROVALS_ENABLED && (
            <DashMetric
              icon="history" label="Pending approvals"
              value={approvalMetrics.pending}
              sub={approvalMetrics.pending === 0 ? 'No invoices awaiting approval' : `${fmtUSD(approvalMetrics.exposure)} in queue`}
              tone={approvalMetrics.pending > 0 ? 'warn' : 'default'}
              onClick={onNav ? () => onNav('approvals') : undefined}
              cta="Open approvals"
            />
          )}
        </div>
      )}

      {/* ── Portfolio budget bar (Dashboard only) ───────────── */}
      {isDashboard && !loading && activeJobs.length > 0 && metrics.budgetTotal > 0 && (() => {
        // Windowed: a % of the full quote is meaningless for a date slice, so the
        // bar/remaining give way to plain windowed spend totals.
        if (isRanged) return (
          <div className="jl-portfolio jl-portfolio-ranged">
            <div className="jl-pf-stat">
              <div className="jl-pf-stat-label">Invoiced · {activeRange.label}</div>
              <div className="num jl-pf-stat-val">{fmtUSD(metrics.invoicedTotal)}</div>
            </div>
            <div className="jl-pf-stat">
              <div className="jl-pf-stat-label">Footage</div>
              <div className="num jl-pf-stat-val">{metrics.lfTracked > 0 ? metrics.lfTracked.toLocaleString() + ' LF' : '—'}</div>
            </div>
            <div className="jl-pf-stat">
              <div className="jl-pf-stat-label">Invoices</div>
              <div className="num jl-pf-stat-val">{metrics.invoiceCount}</div>
            </div>
            <div className="jl-pf-stat">
              <div className="jl-pf-stat-label">Need review</div>
              <div className={`num jl-pf-stat-val${metrics.needsReview > 0 ? ' is-bad' : ' is-ok'}`}>{metrics.needsReview || 'None'}</div>
            </div>
          </div>
        );
        const pct    = metrics.invoicedTotal / metrics.budgetTotal;
        const over   = metrics.invoicedTotal > metrics.budgetTotal;
        const color  = over ? 'var(--bad)' : pct > 0.9 ? 'var(--warn)' : 'var(--accent)';
        return (
          <div className="jl-portfolio">
            <div className="jl-pf-main">
              <div className="jl-pf-top">
                <span className="jl-pf-label">Portfolio budget</span>
                <span className="jl-pf-amounts">
                  <span className="num jl-pf-inv">{fmtUSD(metrics.invoicedTotal)}</span>
                  <span className="jl-pf-of">invoiced of</span>
                  <span className="num jl-pf-tot">{fmtUSD(metrics.budgetTotal)}</span>
                  <span className="num jl-pf-pct" style={{ color }}>{Math.round(pct * 100)}%</span>
                </span>
              </div>
              <div className="jl-pf-track">
                <div className="jl-pf-fill" style={{ width: `${Math.min(pct, 1) * 100}%`, background: color }} />
              </div>
            </div>
            <div className="jl-pf-stats">
              <div className="jl-pf-stat">
                <div className="jl-pf-stat-label">Remaining</div>
                <div className={`num jl-pf-stat-val${metrics.remaining < 0 ? ' is-bad' : ''}`}>{fmtUSD(metrics.remaining)}</div>
              </div>
              <div className="jl-pf-stat">
                <div className="jl-pf-stat-label">Over budget</div>
                {metrics.overExposure > 0 ? (
                  <>
                    <div className="num jl-pf-stat-val is-bad">{fmtUSD(metrics.overExposure)}</div>
                    <div className="jl-pf-stat-sub">{metrics.overJobs} job{metrics.overJobs !== 1 ? 's' : ''}</div>
                  </>
                ) : (
                  <div className="num jl-pf-stat-val is-ok">None</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Dashboard: quick access + Price History widget ──── */}
      {isDashboard && loading && (
        <div className="jl-dash-loading" aria-busy="true" aria-label="Loading dashboard">
          <div className="jl-dash-spin" />
        </div>
      )}
      {isDashboard && !loading && permError && (
        <div className="jl-state">
          <div className="jl-empty-icon" style={{ color: 'var(--warn)' }}><Icon name="flag" size={32} /></div>
          <div className="jl-empty-title">Firestore rules need updating</div>
          <div className="jl-empty-sub">Open Admin Settings, copy the updated rules, and publish them in the Firebase Console. Then reload.</div>
        </div>
      )}
      {isDashboard && !loading && !permError && jobs.length === 0 && (
        <div className="jl-state">
          <div className="jl-empty-icon"><Icon name="delta" size={32} /></div>
          <div className="jl-empty-title">No jobs yet</div>
          <div className="jl-empty-sub">Create your first job to start tracking budgets, invoices, and footage.</div>
          <button className="btn btn-primary" onClick={() => setShowNewJobDrawer(true)}>
            <Icon name="plus" size={14} /> New Job
          </button>
        </div>
      )}
      {isDashboard && !loading && activeJobs.length > 0 && (
        <div className={`jl-dash-cols${priorityJobs.length > 0 ? ' has-alerts' : ''}`}>
          {/* Needs Review — triage rail, left on wide screens */}
          {priorityJobs.length > 0 && (
            <div className="jl-alerts">
              <div className="jl-section-hd">
                <Icon name="flag" size={13} style={{ color: 'var(--warn)' }} />
                <span className="jl-section-title">Needs Review</span>
                <span className="jl-section-count num">{priorityJobs.length}</span>
              </div>
              <div className="jl-alert-list">
                {priorityJobs.map((job) => {
                  const st = jobStats[job.id];
                  return (
                    <button key={job.id} className="jl-alert-row" onClick={() => onSelectJob(job)}>
                      <span className="mono jl-alert-num">{job.jobNumber}</span>
                      <span className="jl-alert-vendor">{st?.vendor}</span>
                      <span className="jl-alert-flags">
                        <Icon name="flag" size={11} />
                        {st?.flaggedCount} item{st?.flaggedCount !== 1 ? 's' : ''} flagged
                      </span>
                      {st?.pctUsed > 1 && (
                        <span className="jl-alert-chip jl-alert-bad">Over budget</span>
                      )}
                      <Icon name="chev" size={12} style={{ color: 'var(--fg-dim)', marginLeft: 'auto', flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent jobs — fills the rest of the row */}
          <div className="jl-dash-recent">
            <div className="jl-section-hd">
              <span className="jl-section-title">Recent jobs</span>
              <span className="jl-section-count num">{activeJobs.length}</span>
              <button className="jl-viewall" onClick={() => onNav && onNav('jobs')}>
                View all jobs <Icon name="chev" size={12} />
              </button>
            </div>
            <div className="jl-grid jl-grid-fill">
              {activeJobs.slice(0, 4).map((job) => (
                <JobCard
                  key={job.id} job={job} stats={jobStats[job.id]}
                  onClick={() => onSelectJob(job)}
                  onToggleActive={() => handleToggleActive(job)}
                  onArchive={() => handleArchive(job)}
                  onDelete={() => handleDelete(job)}
                  onEdit={() => setDrawerJobId(job.id)}
                  procoreCompanyId={procoreCompanyId} ranged={isRanged}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      {isDashboard && !loading && activity.length > 0 && (
        <ActivityFeed items={activity} onOpenInvoice={onOpenJobInvoice} onOpenJob={onSelectJob} />
      )}
      {isDashboard && !loading && !permError && (
        <div className="jl-dash-history">
          <PriceHistoryView user={user} vendorRules={vendorRules} onOpenJob={onOpenJobInvoice} />
        </div>
      )}

      {/* ── Job grid (Jobs page only) ───────────────────────── */}
      {!isDashboard && (loading ? (
        /* UX #13: skeleton cards instead of a bare spinner — the grid layout is
           known, so this reduces layout shift and reads as faster. */
        <div className="jl-grid" aria-busy="true" aria-label="Loading jobs">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="jl-card card jl-skel-card" aria-hidden="true">
              <div className="jl-skel jl-skel-line" style={{ width: '40%' }} />
              <div className="jl-skel jl-skel-line" style={{ width: '70%' }} />
              <div className="jl-skel jl-skel-bar" />
              <div className="jl-skel jl-skel-line" style={{ width: '55%' }} />
            </div>
          ))}
        </div>
      ) : permError ? (
        <div className="jl-state">
          <div className="jl-empty-icon" style={{ color: 'var(--warn)' }}><Icon name="flag" size={32} /></div>
          <div className="jl-empty-title">Firestore rules need updating</div>
          <div className="jl-empty-sub">Open Admin Settings, copy the updated rules, and publish them in the Firebase Console. Then reload.</div>
        </div>
      ) : jobs.length === 0 ? (
        <div className="jl-state">
          <div className="jl-empty-icon"><Icon name="delta" size={32} /></div>
          <div className="jl-empty-title">No jobs yet</div>
          <div className="jl-empty-sub">Create your first job to start tracking budgets, invoices, and footage.</div>
          <button className="btn btn-primary" onClick={() => setShowNewJobDrawer(true)}>
            <Icon name="plus" size={14} /> New Job
          </button>
        </div>
      ) : (
        <>
          {/* ── Toolbar ──────────────────────────────────── */}
          <div className="jl-toolbar">
            <div className="jl-search-wrap">
              <Icon name="search" size={14} className="jl-search-icon" />
              <input
                ref={searchRef}
                className="jl-search-input"
                aria-label="Search jobs"
                placeholder="Search by number, name, or vendor…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActiveExpanded(false); setArchivedExpanded(false); }}
              />
              {query && (
                <button className="jl-search-clear" aria-label="Clear search" onClick={() => { setQuery(''); searchRef.current?.focus(); }}>
                  <Icon name="x" size={12} />
                </button>
              )}
            </div>
            {isSearching && (
              <span className="jl-search-hits">
                {filteredActive.length + filteredArchived.length} result{filteredActive.length + filteredArchived.length !== 1 ? 's' : ''}
              </span>
            )}
            <div className="jl-toolbar-right">
              <button className="btn btn-ghost btn-sm jl-export-btn" onClick={handleExport}
                disabled={sortedActive.length === 0} title="Export current jobs to CSV">
                <Icon name="download" size={13} /> Export
              </button>
              <label className="jl-sort">
                <span className="jl-sort-label">Sort</span>
                <select className="jl-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort jobs">
                  <option value="recent">Most recent</option>
                  <option value="number">Job number</option>
                  <option value="usage">% used</option>
                  <option value="flags">Most flagged</option>
                  <option value="remaining">Least remaining</option>
                </select>
              </label>
              <div className="jl-view-toggle">
                <button
                  className={`jl-vt-btn${viewMode === 'grid' ? ' is-on' : ''}`}
                  onClick={() => setViewMode('grid')} title="Card view"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="0" y="0" width="6" height="6" rx="1"/><rect x="10" y="0" width="6" height="6" rx="1"/>
                    <rect x="0" y="10" width="6" height="6" rx="1"/><rect x="10" y="10" width="6" height="6" rx="1"/>
                  </svg>
                </button>
                <button
                  className={`jl-vt-btn${viewMode === 'table' ? ' is-on' : ''}`}
                  onClick={() => setViewMode('table')} title="Table view"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="0" y="1" width="16" height="2" rx="1"/>
                    <rect x="0" y="6" width="16" height="2" rx="1"/>
                    <rect x="0" y="11" width="16" height="2" rx="1"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* ── Trade filter chips ───────────────────────── */}
          {allTrades.length > 0 && (
            <div className="jl-tradebar">
              <button
                className={`jl-tradefilter${!tradeFilter ? ' is-on' : ''}`}
                onClick={() => setTradeFilter(null)}
              >
                All trades
              </button>
              {allTrades.map((t) => (
                <button
                  key={t}
                  className={`jl-tradefilter${tradeFilter === t ? ' is-on' : ''}`}
                  onClick={() => setTradeFilter((cur) => (cur === t ? null : t))}
                >
                  {t}
                </button>
              ))}
            </div>
          )}

          {/* ── Active filter banner (driven by the "Need review" card) ── */}
          {flaggedOnly && (
            <div className="jl-filter-note">
              <Icon name="flag" size={13} />
              <span>Showing only jobs that need review</span>
              <button className="jl-filter-clear" onClick={() => setFlaggedOnly(false)}>Clear filter</button>
            </div>
          )}

          {/* ── No results ───────────────────────────────── */}
          {flaggedOnly && filteredActive.length === 0 ? (
            <div className="jl-state">
              <div className="jl-empty-icon" style={{ color: 'var(--ok)' }}><Icon name="check" size={28} /></div>
              <div className="jl-empty-title">Nothing needs review</div>
              <div className="jl-empty-sub">No active jobs have flagged items right now.</div>
            </div>
          ) : isSearching && filteredActive.length === 0 && filteredArchived.length === 0 && (
            <div className="jl-state">
              <div className="jl-empty-icon"><Icon name="search" size={28} /></div>
              <div className="jl-empty-title">No jobs match "{query}"</div>
              <div className="jl-empty-sub">Try a different job number, name, or vendor.</div>
            </div>
          )}

          {/* ── Active jobs ──────────────────────────────── */}
          {filteredActive.length > 0 && (
            <>
              <div className="jl-section-hd">
                <span className="jl-section-title">{isSearching ? 'Active' : 'All Jobs'}</span>
                <span className="jl-section-count num">{filteredActive.length}</span>
              </div>

              {viewMode === 'grid' ? (
                <div className="jl-grid">
                  {visibleActive.map((job) => (
                    <JobCard
                      key={job.id} job={job} stats={jobStats[job.id]}
                      onClick={() => onSelectJob(job)}
                      onToggleActive={() => handleToggleActive(job)}
                      onArchive={() => handleArchive(job)}
                      onDelete={() => handleDelete(job)}
                      onEdit={() => setDrawerJobId(job.id)}
                      procoreCompanyId={procoreCompanyId} ranged={isRanged}
                    />
                  ))}
                </div>
              ) : (
                <div className="jlt-wrap">
                  <table className="jlt-table">
                    <thead>
                      <tr className="jlt-thead-row">
                        <th className="jlt-th jlt-check">
                          <input type="checkbox" className="jl-check" aria-label="Select all jobs"
                            checked={visibleActive.length > 0 && visibleActive.every((j) => selected.has(j.id))}
                            ref={(el) => { if (el) el.indeterminate = visibleActive.some((j) => selected.has(j.id)) && !visibleActive.every((j) => selected.has(j.id)); }}
                            onChange={() => toggleSelectAll(visibleActive.map((j) => j.id))} />
                        </th>
                        <th className="jlt-th">Job #</th>
                        <th className="jlt-th">Name</th>
                        <th className="jlt-th">Vendor</th>
                        <th className="jlt-th jlt-th-r">Quoted</th>
                        <th className="jlt-th jlt-th-r">Invoiced</th>
                        {!isRanged && <th className="jlt-th jlt-th-r">Remaining</th>}
                        {!isRanged && <th className="jlt-th">% Used</th>}
                        <th className="jlt-th">Status</th>
                        <th className="jlt-th">Flags</th>
                        <th className="jlt-th"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleActive.map((job) => (
                        <JobTableRow
                          key={job.id} job={job} stats={jobStats[job.id]}
                          onClick={() => onSelectJob(job)}
                          onToggleActive={() => handleToggleActive(job)}
                          onArchive={() => handleArchive(job)}
                          onDelete={() => handleDelete(job)}
                          onEdit={() => setDrawerJobId(job.id)}
                          selectable selected={selected.has(job.id)} onToggleSelect={toggleSelect}
                          ranged={isRanged}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!isSearching && moreActive > 0 && (
                <button className="jl-show-more" onClick={() => setActiveExpanded((v) => !v)}>
                  {activeExpanded
                    ? <><Icon name="chev" size={12} style={{ transform: 'rotate(-90deg)' }}/> Show less</>
                    : <><Icon name="chev" size={12} style={{ transform: 'rotate(90deg)' }}/> Show {moreActive} more job{moreActive !== 1 ? 's' : ''}</>
                  }
                </button>
              )}
            </>
          )}

          {/* ── Archived section ─────────────────────────── */}
          {(archivedJobs.length > 0) && (
            <div className="jl-archived-section">
              {isSearching ? (
                filteredArchived.length > 0 && (
                  <>
                    <div className="jl-section-hd">
                      <Icon name="archive" size={13} style={{ color: 'var(--fg-dim)' }}/>
                      <span className="jl-section-title">Archived</span>
                      <span className="jl-section-count num">{filteredArchived.length}</span>
                    </div>
                    {viewMode === 'grid' ? (
                      <div className="jl-grid jl-grid-archived">
                        {filteredArchived.map((job) => (
                          <JobCard key={job.id} job={job} stats={jobStats[job.id]}
                            onClick={() => onSelectJob(job)} onToggleActive={() => handleToggleActive(job)}
                            onDelete={() => handleDelete(job)} onEdit={() => setDrawerJobId(job.id)}
                            procoreCompanyId={procoreCompanyId} ranged={isRanged} />
                        ))}
                      </div>
                    ) : (
                      <div className="jlt-wrap">
                        <table className="jlt-table">
                          <thead><tr className="jlt-thead-row">
                            <th className="jlt-th jlt-check">
                              <input type="checkbox" className="jl-check" aria-label="Select all archived jobs"
                                checked={filteredArchived.length > 0 && filteredArchived.every((j) => selected.has(j.id))}
                                ref={(el) => { if (el) el.indeterminate = filteredArchived.some((j) => selected.has(j.id)) && !filteredArchived.every((j) => selected.has(j.id)); }}
                                onChange={() => toggleSelectAll(filteredArchived.map((j) => j.id))} />
                            </th>
                            <th className="jlt-th">Job #</th><th className="jlt-th">Name</th>
                            <th className="jlt-th">Vendor</th><th className="jlt-th jlt-th-r">Quoted</th>
                            <th className="jlt-th jlt-th-r">Invoiced</th>{!isRanged && <th className="jlt-th jlt-th-r">Remaining</th>}
                            {!isRanged && <th className="jlt-th">% Used</th>}<th className="jlt-th">Status</th>
                            <th className="jlt-th">Flags</th><th className="jlt-th"></th>
                          </tr></thead>
                          <tbody>
                            {filteredArchived.map((job) => (
                              <JobTableRow key={job.id} job={job} stats={jobStats[job.id]}
                                onClick={() => onSelectJob(job)} onToggleActive={() => handleToggleActive(job)}
                                onDelete={() => handleDelete(job)} onEdit={() => setDrawerJobId(job.id)}
                                onArchive={() => handleArchive(job)}
                                selectable selected={selected.has(job.id)} onToggleSelect={toggleSelect} ranged={isRanged} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )
              ) : (
                <>
                  <button className="jl-archived-toggle" onClick={() => { setShowArchived((v) => !v); setArchivedExpanded(false); }}>
                    <Icon name="archive" size={13} />
                    <span>Archived ({archivedJobs.length})</span>
                    <Icon name="chev" size={12} style={{ transform: showArchived ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                  </button>
                  {showArchived && (
                    <>
                      {viewMode === 'grid' ? (
                        <div className="jl-grid jl-grid-archived">
                          {visibleArchived.map((job) => (
                            <JobCard key={job.id} job={job} stats={jobStats[job.id]}
                              onClick={() => onSelectJob(job)} onToggleActive={() => handleToggleActive(job)}
                              onDelete={() => handleDelete(job)} onEdit={() => setDrawerJobId(job.id)}
                              procoreCompanyId={procoreCompanyId} ranged={isRanged} />
                          ))}
                        </div>
                      ) : (
                        <div className="jlt-wrap">
                          <table className="jlt-table">
                            <thead><tr className="jlt-thead-row">
                              <th className="jlt-th jlt-check">
                                <input type="checkbox" className="jl-check" aria-label="Select all archived jobs"
                                  checked={visibleArchived.length > 0 && visibleArchived.every((j) => selected.has(j.id))}
                                  ref={(el) => { if (el) el.indeterminate = visibleArchived.some((j) => selected.has(j.id)) && !visibleArchived.every((j) => selected.has(j.id)); }}
                                  onChange={() => toggleSelectAll(visibleArchived.map((j) => j.id))} />
                              </th>
                              <th className="jlt-th">Job #</th><th className="jlt-th">Name</th>
                              <th className="jlt-th">Vendor</th><th className="jlt-th jlt-th-r">Quoted</th>
                              <th className="jlt-th jlt-th-r">Invoiced</th>{!isRanged && <th className="jlt-th jlt-th-r">Remaining</th>}
                              {!isRanged && <th className="jlt-th">% Used</th>}<th className="jlt-th">Status</th>
                              <th className="jlt-th">Flags</th><th className="jlt-th"></th>
                            </tr></thead>
                            <tbody>
                              {visibleArchived.map((job) => (
                                <JobTableRow key={job.id} job={job} stats={jobStats[job.id]}
                                  onClick={() => onSelectJob(job)} onToggleActive={() => handleToggleActive(job)}
                                  onDelete={() => handleDelete(job)} onEdit={() => setDrawerJobId(job.id)}
                                  onArchive={() => handleArchive(job)}
                                  selectable selected={selected.has(job.id)} onToggleSelect={toggleSelect} ranged={isRanged} />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {moreArchived > 0 && (
                        <button className="jl-show-more" onClick={() => setArchivedExpanded((v) => !v)}>
                          {archivedExpanded
                            ? <><Icon name="chev" size={12} style={{ transform: 'rotate(-90deg)' }}/> Show less</>
                            : <><Icon name="chev" size={12} style={{ transform: 'rotate(90deg)' }}/> Show {moreArchived} more archived</>
                          }
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </>
      ))}

      {pendingArchive && (
        <ArchiveConfirmModal
          job={pendingArchive}
          onConfirm={handleConfirmArchive}
          onCancel={() => setPendingArchive(null)}
        />
      )}
      {pendingDelete && (
        <DeleteConfirmModal
          job={pendingDelete}
          onConfirm={handleConfirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
      {confirmCfg && <ConfirmModal cfg={confirmCfg} onClose={() => setConfirmCfg(null)} />}

      {/* ── Bulk action bar — floats while a table selection is active ── */}
      {!isDashboard && viewMode === 'table' && selectedJobs.length > 0 && (
        <div className="jl-bulkbar" role="region" aria-label="Bulk actions">
          <span className="jl-bb-count"><strong>{selectedJobs.length}</strong> selected</span>
          <div className="jl-bb-actions">
            {selActive.length > 0 && (
              <button className="jl-bb-btn" onClick={handleBulkArchive}><Icon name="archive" size={13} /> Archive</button>
            )}
            {selArchived.length > 0 && (
              <button className="jl-bb-btn" onClick={handleBulkRestore}><Icon name="eye" size={13} /> Restore</button>
            )}
            <button className="jl-bb-btn" onClick={handleBulkExport}><Icon name="download" size={13} /> Export</button>
            <button className="jl-bb-btn jl-bb-del" onClick={handleBulkDelete}><Icon name="trash" size={13} /> Delete</button>
          </div>
          <button className="jl-bb-clear" onClick={clearSelection}><Icon name="x" size={13} /> Clear</button>
        </div>
      )}
      <JobListStyles />
      <ToastContainer toasts={toasts} />
    </div>
    </>
  );
}

const JobListStyles = () => (
  <style>{`
    /* ── Page layout ─────────────────────────────────────────── */
    .jl-view { width: 100%; max-width: var(--app-max); margin: 0 auto; padding: 40px var(--app-pad) 80px; }
    .jl-hd {
      display: flex; align-items: flex-start; justify-content: space-between;
      gap: 16px; margin-bottom: 32px; flex-wrap: wrap;
    }
    .jl-eyebrow { font-family: "IBM Plex Mono", ui-monospace, monospace; font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 8px; }
    .jl-welcome { margin: 0; font-size: 30px; font-weight: 500; letter-spacing: -0.015em; color: var(--fg); }
    .jl-welcome strong { font-weight: 700; }
    .jl-welcome-sub { margin-top: 4px; font-size: 13.5px; color: var(--fg-muted); }

    /* ── Header actions (date range + New Job) ─────────────────── */
    .jl-hd-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .jl-range { display: inline-flex; align-items: center; gap: 6px; position: relative; }
    .jl-range-icon { color: var(--fg-dim); flex-shrink: 0; }
    .jl-range-select {
      appearance: none; -webkit-appearance: none;
      background: var(--surface-2); color: var(--fg);
      border: 1px solid var(--border); border-radius: var(--r);
      font: inherit; font-size: 12.5px; font-weight: 500;
      padding: 6px 26px 6px 10px; cursor: pointer; height: 32px;
      background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%237a8390' d='M0 0h10L5 6z'/></svg>");
      background-repeat: no-repeat; background-position: right 9px center;
      transition: border-color .12s, background-color .12s;
    }
    .jl-range-select:hover { border-color: var(--border-strong); background-color: var(--surface-3); }
    .jl-range-select:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 2px oklch(from var(--accent) l c h / 0.18); }
    .jl-range-custom { display: inline-flex; align-items: center; gap: 5px; }
    .jl-range-date {
      background: var(--surface-2); color: var(--fg);
      border: 1px solid var(--border); border-radius: var(--r);
      font: inherit; font-size: 12px; padding: 5px 8px; height: 32px;
    }
    .jl-range-date:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 2px oklch(from var(--accent) l c h / 0.18); }
    .jl-range-dash { color: var(--fg-dim); }
    .jl-range-clear {
      appearance: none; border: 1px solid var(--border); background: var(--surface-2);
      border-radius: var(--r); padding: 6px; cursor: pointer; color: var(--fg-muted);
      display: grid; place-items: center; height: 32px; width: 32px;
      transition: background .12s, color .12s;
    }
    .jl-range-clear:hover { background: var(--surface-3); color: var(--fg); }

    /* ── Active date-range banner ──────────────────────────────── */
    .jl-range-note {
      display: flex; align-items: center; gap: 8px;
      padding: 8px 14px; margin-bottom: 20px;
      background: var(--info-bg); border: 1px solid oklch(from var(--info) l calc(c*.5) h / .35);
      border-radius: var(--r); font-size: 12.5px; font-weight: 500; color: var(--info);
    }
    .jl-range-note svg { flex-shrink: 0; }
    .jl-range-note strong { font-weight: 700; }
    .jl-range-note .jl-filter-clear { color: var(--info); margin-left: auto; }

    /* Windowed portfolio strip — plain spend totals, no budget ratio. */
    .jl-portfolio-ranged { gap: 36px; }

    .jl-new-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .jl-new-input { width: 160px; }
    .jl-cf-procore {
      color: oklch(0.62 0.12 250); background: oklch(0.62 0.12 250 / 0.1);
      border-color: oklch(0.62 0.12 250 / 0.3);
      text-decoration: none; cursor: pointer;
    }
    .jl-cf-procore:hover { background: oklch(0.62 0.12 250 / 0.18); }
    .jl-link-row {
      display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
      padding: 8px 0 4px;
      border-top: 1px dashed var(--border);
    }
    .jl-link-select { flex: 1; min-width: 0; font-size: 12px; padding: 5px 8px; }
    .jl-procore-btn {
      appearance: none; border: 1px solid var(--border); background: var(--surface-2);
      border-radius: var(--r); padding: 5px 10px; font: inherit; font-size: 12px;
      font-weight: 500; color: var(--fg-muted); cursor: pointer;
      display: inline-flex; align-items: center; gap: 5px;
      transition: color .12s, background .12s, border-color .12s;
      white-space: nowrap;
    }
    .jl-procore-btn:hover:not(:disabled) { color: var(--fg); background: var(--surface-3); border-color: var(--border-strong); }
    .jl-procore-btn:disabled { opacity: 0.5; cursor: default; }
    .jl-procore-select { width: 280px; }
    .jl-new-name  { width: 200px; }

    /* ── Summary metrics ─────────────────────────────────────── */
    .jl-metrics {
      display: grid; grid-template-columns: repeat(5, 1fr);
      gap: 14px; margin-bottom: 32px;
    }
    .dm {
      position: relative;
      padding: 18px 18px 16px;
      background: var(--surface); border: 1px solid var(--border);
      border-top: 3px solid var(--border);
      border-radius: var(--r-lg); box-shadow: var(--shadow-2);
      display: flex; flex-direction: column;
    }
    /* Toned cards get a faint top wash in their status color for stronger identity. */
    .dm-ok   { border-top-color: var(--ok);   background: linear-gradient(180deg, oklch(from var(--ok)   l c h / 0.08), transparent 55%), var(--surface); }
    .dm-warn { border-top-color: var(--warn); background: linear-gradient(180deg, oklch(from var(--warn) l c h / 0.08), transparent 55%), var(--surface); }
    .dm-info { border-top-color: var(--info); background: linear-gradient(180deg, oklch(from var(--info) l c h / 0.08), transparent 55%), var(--surface); }
    .dm-bad  { border-top-color: var(--bad);  background: linear-gradient(180deg, oklch(from var(--bad)  l c h / 0.08), transparent 55%), var(--surface); }
    /* KPI layout: label + icon share the top row, big mono value is the hero. */
    .dm-top  { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 16px; }
    .dm-icon {
      width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
      display: grid; place-items: center;
      background: var(--surface-2); color: var(--fg-dim);
    }
    .dm-ok   .dm-icon { background: var(--ok-bg);   color: var(--ok);   }
    .dm-warn .dm-icon { background: var(--warn-bg); color: var(--warn); }
    .dm-info .dm-icon { background: var(--info-bg); color: var(--info); }
    .dm-bad  .dm-icon { background: var(--bad-bg);  color: var(--bad);  }
    .dm-value { font-size: 28px; font-weight: 700; letter-spacing: -0.03em; line-height: 1.1; color: var(--fg); }
    .dm-label { font-size: 10.5px; color: var(--fg-dim); text-transform: uppercase; letter-spacing: 0.07em; font-weight: 600; }
    .dm-sub   { font-size: 11.5px; color: var(--fg-muted); margin-top: 6px; line-height: 1.4; }
    /* Clickable KPI cards (Need review → filter, Pending approvals → navigate).
       Affordance is an accent ring + lift on hover so the colored top rule stays. */
    .dm-clickable { cursor: pointer; transition: box-shadow .18s cubic-bezier(.23,1,.32,1), transform .14s cubic-bezier(.23,1,.32,1); }
    .dm-clickable:hover { box-shadow: var(--glow-accent); transform: translateY(-2px); }
    /* Tactile press — confirms the click without overshooting the hover lift. */
    .dm-clickable:active { transform: translateY(0) scale(.985); transition-duration: .07s; }
    .dm-clickable:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--accent); }
    .dm-active { box-shadow: 0 0 0 2px oklch(from var(--accent) l c h / 0.55); }
    .dm-cta {
      margin-top: 10px; display: inline-flex; align-items: center; gap: 4px;
      font-size: 11px; font-weight: 600; color: var(--accent);
    }
    .dm-cta svg { transition: transform .12s; }
    .dm-clickable:hover .dm-cta svg { transform: translateX(2px); }

    /* ── Portfolio budget bar ──────────────────────────────────────
       Aggregate running total across every active job: how much of the combined
       quoted budget has been invoiced, what's left, and dollars over budget. */
    .jl-portfolio {
      display: flex; align-items: center; gap: 28px;
      padding: 16px 20px; margin-bottom: 32px;
      background: var(--surface); border: 1px solid var(--border);
      border-radius: var(--r-lg); box-shadow: var(--shadow-1);
    }
    .jl-pf-main { flex: 1; min-width: 0; }
    .jl-pf-top {
      display: flex; align-items: baseline; justify-content: space-between;
      gap: 12px; margin-bottom: 10px; flex-wrap: wrap;
    }
    .jl-pf-label { font-size: 11px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase; color: var(--fg-dim); }
    .jl-pf-amounts { display: inline-flex; align-items: baseline; gap: 7px; flex-wrap: wrap; }
    .jl-pf-inv { font-size: 15px; font-weight: 700; color: var(--fg); letter-spacing: -0.02em; }
    .jl-pf-of  { font-size: 12px; color: var(--fg-dim); }
    .jl-pf-tot { font-size: 13px; color: var(--fg-muted); }
    .jl-pf-pct { font-size: 13px; font-weight: 700; margin-left: 4px; }
    .jl-pf-track { height: 8px; background: var(--surface-3); border-radius: 999px; overflow: hidden; }
    .jl-pf-fill  { height: 100%; border-radius: 999px; transition: width .5s cubic-bezier(.25,.46,.45,.94); }
    .jl-pf-stats { display: flex; gap: 24px; flex-shrink: 0; }
    .jl-pf-stat  { display: flex; flex-direction: column; gap: 2px; min-width: 78px; }
    .jl-pf-stat-label { font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--fg-dim); }
    .jl-pf-stat-val   { font-size: 17px; font-weight: 700; letter-spacing: -0.02em; color: var(--fg); }
    .jl-pf-stat-val.is-bad { color: var(--bad); }
    .jl-pf-stat-val.is-ok  { color: var(--ok); }
    .jl-pf-stat-sub   { font-size: 10.5px; color: var(--fg-dim); }

    /* ── Dashboard entrance motion ───────────────────────────────────
       The landing view assembles in a quiet top-to-bottom cascade so the
       eye lands on the header, then the KPIs, then the detail. Only
       transform + opacity animate (GPU-composited); a fast ease-out curve
       keeps it felt, not watched. fill-mode is 'backwards' on purpose —
       it shows the from-state during the stagger delay but releases the
       transform once done, so card hover-lifts and presses still work.
       Fully disabled under reduced-motion (elements just appear). */
    @keyframes jl-rise   { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
    @keyframes jl-grow-x { from { transform: scaleX(0); } to { transform: scaleX(1); } }

    @media (prefers-reduced-motion: no-preference) {
      /* Containers that have no individually-animated children rise as a unit.
         (.jl-dash-cols is intentionally absent — its rows and cards cascade
         on their own below, so animating the wrapper too would double the
         movement.) */
      .jl-hd,
      .jl-metrics .dm,
      .jl-portfolio,
      .jl-activity,
      .jl-dash-history { animation: jl-rise .52s cubic-bezier(.23,1,.32,1) backwards; }

      .jl-hd            { animation-delay: 0s;   }
      .jl-metrics .dm:nth-child(1) { animation-delay: .05s; }
      .jl-metrics .dm:nth-child(2) { animation-delay: .10s; }
      .jl-metrics .dm:nth-child(3) { animation-delay: .15s; }
      .jl-metrics .dm:nth-child(4) { animation-delay: .20s; }
      .jl-metrics .dm:nth-child(5) { animation-delay: .25s; }
      .jl-portfolio     { animation-delay: .22s; }
      .jl-activity      { animation-delay: .34s; }
      .jl-dash-history  { animation-delay: .42s; }

      /* Budget bar sweeps out from its left edge as the portfolio card lands. */
      .jl-pf-fill { transform-origin: left center; animation: jl-grow-x .8s cubic-bezier(.23,1,.32,1) .34s backwards; }

      /* Triage rows cascade just behind the section they live in. */
      .jl-alert-row { animation: jl-rise .46s cubic-bezier(.23,1,.32,1) backwards; }
      .jl-alert-row:nth-child(1) { animation-delay: .34s; }
      .jl-alert-row:nth-child(2) { animation-delay: .40s; }
      .jl-alert-row:nth-child(3) { animation-delay: .46s; }
      .jl-alert-row:nth-child(4) { animation-delay: .52s; }

      /* Recent job cards cascade alongside the triage rail. */
      .jl-dash-recent .jl-card { animation: jl-rise .46s cubic-bezier(.23,1,.32,1) backwards; }
      .jl-dash-recent .jl-card:nth-child(1) { animation-delay: .36s; }
      .jl-dash-recent .jl-card:nth-child(2) { animation-delay: .42s; }
      .jl-dash-recent .jl-card:nth-child(3) { animation-delay: .48s; }
      .jl-dash-recent .jl-card:nth-child(4) { animation-delay: .54s; }
    }

    /* ── Toolbar ────────────────────────────────────────────────── */
    .jl-toolbar {
      display: flex; align-items: center; gap: 10px;
      margin-bottom: 20px;
      padding: 10px 14px;
      background: var(--surface); border: 1px solid var(--border);
      border-radius: var(--r-lg);
    }
    .jl-search-wrap {
      position: relative; flex: 1; max-width: 380px;
      display: flex; align-items: center;
    }
    .jl-toolbar-right { display: flex; align-items: center; gap: 10px; margin-left: auto; }
    .jl-sort { display: inline-flex; align-items: center; gap: 7px; }
    .jl-sort-label { font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--fg-dim); }
    .jl-sort-select {
      appearance: none; -webkit-appearance: none;
      background: var(--surface-2); color: var(--fg);
      border: 1px solid var(--border); border-radius: var(--r);
      font: inherit; font-size: 12.5px; font-weight: 500;
      padding: 5px 26px 5px 10px; cursor: pointer; height: 30px;
      background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%237a8390' d='M0 0h10L5 6z'/></svg>");
      background-repeat: no-repeat; background-position: right 9px center;
      transition: border-color .12s, background-color .12s;
    }
    .jl-sort-select:hover { border-color: var(--border-strong); background-color: var(--surface-3); }
    .jl-sort-select:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 2px oklch(from var(--accent) l c h / 0.18); }
    .jl-view-toggle {
      display: flex; gap: 2px;
      background: var(--surface-2); border: 1px solid var(--border);
      border-radius: var(--r); padding: 2px;
    }
    .jl-vt-btn {
      appearance: none; border: 0; background: transparent;
      padding: 5px 8px; border-radius: 4px; cursor: pointer;
      color: var(--fg-dim); display: grid; place-items: center;
      transition: background .1s, color .1s;
    }
    .jl-vt-btn:hover { color: var(--fg); }
    .jl-vt-btn.is-on { background: var(--bg); color: var(--fg); box-shadow: var(--shadow-1); }
    .jl-search-icon {
      position: absolute; left: 10px; color: var(--fg-dim); pointer-events: none; flex-shrink: 0;
    }
    .jl-search-input {
      width: 100%; padding: 7px 32px 7px 34px;
      background: var(--surface); border: 1px solid var(--border);
      border-radius: var(--r); font: inherit; font-size: 13px; color: var(--fg);
      outline: none; transition: border-color .12s, box-shadow .12s;
    }
    .jl-search-input::placeholder { color: var(--fg-dim); }
    .jl-search-input:focus { border-color: var(--accent); box-shadow: 0 0 0 2px oklch(from var(--accent) l c h / 0.15); }
    .jl-search-clear {
      position: absolute; right: 6px;
      appearance: none; border: 0; background: transparent;
      padding: 4px; border-radius: 4px; cursor: pointer;
      color: var(--fg-dim); display: grid; place-items: center;
    }
    .jl-search-clear:hover { color: var(--fg); background: var(--surface-3); }
    .jl-search-hits { font-size: 12px; color: var(--fg-dim); white-space: nowrap; }

    /* ── Active filter banner ──────────────────────────────────────── */
    .jl-filter-note {
      display: flex; align-items: center; gap: 8px;
      padding: 8px 14px; margin-bottom: 16px;
      background: var(--warn-bg); border: 1px solid oklch(from var(--warn) l calc(c*.5) h / .35);
      border-radius: var(--r); font-size: 12.5px; font-weight: 500; color: var(--warn);
    }
    .jl-filter-note svg { flex-shrink: 0; }
    .jl-filter-clear {
      appearance: none; border: 0; background: transparent; margin-left: auto;
      font: inherit; font-size: 12px; font-weight: 600; color: var(--warn);
      cursor: pointer; text-decoration: underline; padding: 2px 4px; border-radius: 4px;
    }
    .jl-filter-clear:hover { color: var(--fg); }

    /* ── Trade filter bar ────────────────────────────────────────── */
    .jl-tradebar {
      display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px;
    }
    .jl-tradefilter {
      appearance: none; cursor: pointer; font: inherit;
      font-size: 11.5px; font-weight: 500; line-height: 1;
      padding: 6px 12px; border-radius: 999px;
      border: 1px solid var(--border); background: var(--surface-2); color: var(--fg-muted);
      transition: background .12s, color .12s, border-color .12s;
    }
    .jl-tradefilter:hover { background: var(--surface-3); color: var(--fg); }
    .jl-tradefilter.is-on {
      background: oklch(from var(--accent) l c h / .14); color: var(--accent);
      border-color: oklch(from var(--accent) l c h / .4);
    }

    /* ── Trade pills (cards / rows) ──────────────────────────────── */
    .jl-trades { display: flex; flex-wrap: wrap; gap: 4px; margin: 8px 0 0; }
    .jl-trade {
      font-size: 10px; font-weight: 600; letter-spacing: .02em; line-height: 1;
      padding: 3px 7px; border-radius: 4px;
      background: var(--surface-3); color: var(--fg-muted); border: 1px solid var(--border);
      white-space: nowrap;
    }
    .jl-trade.is-click { cursor: pointer; transition: background .12s, color .12s; }
    .jl-trade.is-click:hover { background: var(--surface-2); color: var(--fg); }
    .jl-trade.is-active { background: oklch(from var(--accent) l c h / .14); color: var(--accent); border-color: oklch(from var(--accent) l c h / .4); }
    .jl-trade-more { color: var(--fg-dim); }
    .jlt-name .jl-trades { margin-top: 4px; }

    /* ── Trade picker (drawers) ──────────────────────────────────── */
    .jed-trades { display: flex; flex-direction: column; gap: 12px; }
    .jed-trade-chips { display: flex; flex-wrap: wrap; gap: 6px; }
    .jed-trade-chip {
      appearance: none; cursor: pointer; font: inherit;
      font-size: 12px; font-weight: 500; line-height: 1;
      display: inline-flex; align-items: center; gap: 4px;
      padding: 7px 11px; border-radius: 999px;
      border: 1px solid var(--border); background: var(--surface-2); color: var(--fg-muted);
      transition: background .12s, color .12s, border-color .12s;
    }
    .jed-trade-chip:hover { background: var(--surface-3); color: var(--fg); }
    .jed-trade-chip.is-on {
      background: oklch(from var(--accent) l c h / .14); color: var(--accent);
      border-color: oklch(from var(--accent) l c h / .45);
    }
    .jed-trade-add { display: flex; gap: 8px; }
    .jed-trade-add .input { flex: 1; }

    /* ── Show more button ────────────────────────────────────────── */
    .jl-show-more {
      appearance: none; border: 1px solid var(--border); background: var(--surface-2);
      border-radius: var(--r); padding: 7px 16px; font: inherit; font-size: 12.5px;
      font-weight: 500; color: var(--fg-muted); cursor: pointer;
      display: inline-flex; align-items: center; gap: 6px;
      margin-bottom: 28px;
      transition: color .12s, background .12s, border-color .12s;
    }
    .jl-show-more:hover { color: var(--fg); background: var(--surface-3); border-color: var(--border-strong); }

    /* ── Section heading ──────────────────────────────────────── */
    .jl-section-hd { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
    .jl-section-title { font-size: 13px; font-weight: 600; letter-spacing: -0.005em; }
    .jl-section-count {
      font-size: 10.5px; padding: 1px 8px; border-radius: 999px;
      background: var(--surface-2); color: var(--fg-dim); border: 1px solid var(--border);
    }

    /* ── Dashboard: recent-jobs strip, view-all CTA, Price History widget ── */
    .jl-dash-loading { display: flex; align-items: center; justify-content: center; min-height: 30vh; }
    .jl-dash-spin { width: 26px; height: 26px; border-radius: 50%; border: 2.5px solid var(--border); border-top-color: var(--accent); animation: jl-sp .7s linear infinite; }
    /* Dashboard mid-section: Needs Review (triage rail) beside Recent jobs on
       wide screens; stacks when there are no alerts or on narrower viewports. */
    .jl-dash-cols { display: grid; grid-template-columns: 1fr; gap: 24px; margin-bottom: 36px; }
    .jl-dash-cols.has-alerts { grid-template-columns: minmax(330px, 440px) minmax(0, 1fr); align-items: start; }
    .jl-dash-cols .jl-alerts,
    .jl-dash-cols .jl-dash-recent { margin-bottom: 0; }
    @media (max-width: 1100px) { .jl-dash-cols.has-alerts { grid-template-columns: 1fr; } }
    .jl-dash-recent { margin-bottom: 36px; }
    .jl-viewall {
      appearance: none; border: 0; background: transparent; margin-left: auto;
      font: inherit; font-size: 12.5px; font-weight: 600; color: var(--accent); cursor: pointer;
      display: inline-flex; align-items: center; gap: 4px; padding: 4px 6px; border-radius: var(--r);
      transition: background .12s;
    }
    .jl-viewall:hover { background: oklch(from var(--accent) l c h / 0.12); }
    .jl-dash-history { margin-top: 8px; border-top: 1px solid var(--border); }
    /* The embedded Price History view brings its own padding; trim the top so it
       sits flush under the divider as a dashboard section. */
    .jl-dash-history .ph-view { padding-top: 28px; }

    /* ── Recent activity feed ────────────────────────────────── */
    .jl-activity { margin-bottom: 36px; }
    .jl-act-feed { display: flex; flex-direction: column; gap: 3px; }
    .jl-actv-row {
      appearance: none; border: 1px solid var(--border); background: var(--surface);
      border-radius: var(--r-lg); padding: 10px 14px; font: inherit; cursor: pointer;
      display: flex; align-items: center; gap: 12px; text-align: left; width: 100%;
      transition: border-color .14s, background .14s, transform .12s cubic-bezier(.23,1,.32,1);
    }
    .jl-actv-row:hover { border-color: var(--border-strong); background: var(--surface-2); }
    .jl-actv-row:active { transform: scale(.995); }
    .jl-actv-icon { width: 28px; height: 28px; border-radius: 8px; display: grid; place-items: center; flex-shrink: 0; background: var(--surface-3); color: var(--fg-dim); }
    .jl-actv-invoice { background: var(--info-bg); color: var(--info); }
    .jl-actv-job { background: oklch(from var(--accent) l c h / .12); color: var(--accent); }
    .jl-actv-text { flex: 1; min-width: 0; font-size: 13px; color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .jl-actv-text strong { color: var(--fg); font-weight: 600; }
    .jl-actv-meta { color: var(--fg-dim); }
    .jl-actv-amt { font-size: 12.5px; font-weight: 600; color: var(--fg); flex-shrink: 0; }
    .jl-actv-time { font-size: 11.5px; color: var(--fg-dim); white-space: nowrap; flex-shrink: 0; min-width: 56px; text-align: right; }

    /* ── Priority alerts ─────────────────────────────────────── */
    .jl-alerts { margin-bottom: 32px; }
    .jl-alert-list { display: flex; flex-direction: column; gap: 3px; }
    .jl-alert-row {
      appearance: none; border: 1px solid var(--border-strong);
      background: var(--surface); border-radius: var(--r-lg);
      padding: 11px 16px; font: inherit; cursor: pointer;
      display: flex; align-items: center; gap: 14px; text-align: left;
      transition: border-color .14s, background .14s, transform .12s cubic-bezier(.23,1,.32,1);
    }
    .jl-alert-row:hover { border-color: var(--warn); background: oklch(from var(--warn) l c h / 0.04); }
    .jl-alert-row:active { transform: scale(.99); }
    /* Chevron drifts toward the user on hover, signalling "opens". */
    .jl-alert-row > svg:last-child { transition: transform .14s cubic-bezier(.23,1,.32,1); }
    .jl-alert-row:hover > svg:last-child { transform: translateX(3px); }
    .jl-alert-num    { font-size: 13px; font-weight: 600; min-width: 100px; }
    .jl-alert-vendor { font-size: 12.5px; color: var(--fg-muted); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .jl-alert-flags  { display: flex; align-items: center; gap: 5px; font-size: 12px; color: var(--warn); white-space: nowrap; }
    .jl-alert-chip   { font-size: 10.5px; padding: 2px 8px; border-radius: 999px; border: 1px solid; white-space: nowrap; }
    .jl-alert-bad    { color: var(--bad); border-color: oklch(from var(--bad) l calc(c*.5) h / .4); background: var(--bad-bg); }

    /* ── Job cards ───────────────────────────────────────────── */
    /* auto-fit (not auto-fill): when only a few jobs are shown, empty tracks
       collapse so the cards stretch left-to-right and fill the row, matching
       the full width of the table view instead of bunching narrow on the left. */
    .jl-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); gap: 14px; margin-bottom: 40px; }
    /* Dashboard "Recent jobs": stretch the few cards to fill the wide row
       (auto-fit collapses empty tracks) instead of leaving trailing gaps. */
    .jl-grid-fill { grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); }
    /* UX #13: job-card skeletons (shimmer) shown while the grid loads. */
    .jl-skel-card { padding: 18px; display: flex; flex-direction: column; gap: 12px; }
    .jl-skel { border-radius: 6px; background: linear-gradient(90deg, var(--surface-2) 25%, var(--surface-3) 37%, var(--surface-2) 63%); background-size: 400% 100%; animation: jl-shimmer 1.4s ease infinite; }
    .jl-skel-line { height: 12px; }
    .jl-skel-bar { height: 8px; width: 100%; margin: 4px 0; }
    @keyframes jl-shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }
    @media (prefers-reduced-motion: reduce) { .jl-skel { animation: none; } .jl-card:hover, .dm-clickable:hover { transform: none; } }
    .jl-grid-archived { margin-top: 12px; margin-bottom: 0; }

    .jl-card {
      width: 100%; font: inherit;
      background: var(--surface); border: 1px solid var(--border);
      border-radius: var(--r-lg); box-shadow: var(--shadow-1);
      padding: 18px 20px; text-align: left; cursor: pointer;
      display: flex; flex-direction: column; gap: 10px;
      transition: border-color .12s, background .12s, box-shadow .15s, transform .15s;
      outline: none;
      position: relative;
    }
    .jl-card:hover { border-color: var(--accent); background: var(--surface-2); box-shadow: var(--glow-accent); transform: translateY(-2px); }
    .jl-card:focus-visible { box-shadow: 0 0 0 2px var(--accent); }
    .jl-card-flagged { border-color: oklch(from var(--warn) l calc(c*.5) h / .45); }
    .jl-card-flagged:hover { border-color: var(--warn); }
    .jl-card-archived { opacity: 0.6; }

    .jl-card-top     { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .jl-card-top-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
    .jl-card-num     { font-size: 14px; font-weight: 600; color: var(--fg); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .jl-card-status  { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 500; }
    .jl-cs-dot       { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
    .jl-card-vendor  { font-size: 12px; color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .jl-archived-tag {
      font-size: 10px; padding: 2px 7px; border-radius: 999px;
      background: var(--surface-3); color: var(--fg-dim);
      border: 1px solid var(--border); font-weight: 500;
      text-transform: uppercase; letter-spacing: 0.05em;
    }

    /* ── Job number / name rows ──────────────────────────────── */
    .jl-card-num-wrap { min-width: 0; flex: 1; }
    .jl-card-num-row  { display: flex; align-items: center; gap: 5px; }
    .jl-card-name-row { display: flex; align-items: center; gap: 5px; }
    .jl-card-name     { font-size: 12.5px; font-weight: 500; color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }

    .jl-rename-btn {
      appearance: none; border: 0; background: transparent;
      padding: 2px; border-radius: 3px; cursor: pointer;
      color: var(--fg-dim); display: grid; place-items: center;
      opacity: 0; transition: opacity .1s, color .1s, background .1s;
      flex-shrink: 0;
    }
    .jl-card:hover .jl-rename-btn { opacity: 1; }
    .jl-rename-btn:hover { color: var(--accent); background: oklch(from var(--accent) l c h / 0.1); }

    .jl-rename-input {
      font: inherit; font-size: 13.5px; font-weight: 600;
      background: var(--surface-3); border: 1px solid var(--accent);
      border-radius: 4px; padding: 1px 6px; color: var(--fg);
      outline: none; box-shadow: 0 0 0 2px oklch(from var(--accent) l c h / 0.15);
      width: 100%; max-width: 160px;
    }
    .jl-rename-name { font-weight: 500; font-size: 12.5px; }

    .jl-add-name-btn {
      appearance: none; border: 0; background: transparent; padding: 0;
      font: inherit; font-size: 11px; color: var(--fg-dim); cursor: pointer;
      opacity: 0; transition: opacity .1s, color .1s;
    }
    .jl-card:hover .jl-add-name-btn { opacity: 1; }
    .jl-add-name-btn:hover { color: var(--accent); }

    /* Card action buttons — visible on card hover */
    .jl-card-acts {
      display: flex; gap: 2px;
      opacity: 0; transition: opacity .12s;
    }
    .jl-card:hover .jl-card-acts { opacity: 1; }
    .jl-act-btn {
      appearance: none; border: 1px solid var(--border);
      background: var(--surface-2); border-radius: 5px;
      padding: 4px 5px; cursor: pointer;
      color: var(--fg-dim); display: grid; place-items: center;
      transition: color .1s, background .1s, border-color .1s;
    }
    .jl-act-btn:hover { color: var(--fg); background: var(--surface-3); border-color: var(--border-strong); }
    .jl-act-del:hover { color: var(--bad); background: var(--bad-bg); border-color: oklch(from var(--bad) l calc(c*.5) h / .4); }

    .jl-budget-bar  { display: flex; align-items: center; gap: 8px; }
    .jl-bb-track    { flex: 1; height: 4px; background: var(--surface-3); border-radius: 999px; overflow: hidden; }
    .jl-bb-fill     { height: 100%; border-radius: 999px; transition: width .4s; }
    .jl-bb-pct      { font-size: 11px; font-weight: 600; flex-shrink: 0; }
    .jl-card-amounts{ display: flex; align-items: baseline; gap: 6px; font-size: 12px; }
    .jl-ca-inv      { color: var(--fg); font-size: 14px; font-weight: 600; }
    .jl-ca-sep      { color: var(--fg-dim); }
    .jl-ca-qtd      { color: var(--fg-dim); }

    .jl-card-quoted { display: flex; align-items: center; justify-content: space-between; font-size: 13px; }
    .jl-cq-label    { font-size: 10px; color: var(--fg-dim); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 500; }
    .jl-card-setup  { font-size: 12px; color: var(--fg-dim); font-style: italic; }

    .jl-card-footer {
      display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
      padding-top: 8px; border-top: 1px solid var(--border);
    }
    .jl-cf-arrow  { color: var(--fg-dim); margin-left: auto; flex-shrink: 0; opacity: 0.5; }
    .jl-cf-chip   {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 10.5px; color: var(--fg-dim);
      background: var(--surface-2); padding: 2px 8px;
      border-radius: 999px; border: 1px solid var(--border);
    }
    .jl-cf-flag   { color: var(--warn); background: var(--warn-bg); border-color: oklch(from var(--warn) l calc(c*.5) h / .35); }
    .jl-card-date { font-size: 11px; color: var(--fg-dim); }

    /* ── Table view ─────────────────────────────────────────── */
    .jlt-wrap {
      margin-bottom: 28px; overflow-x: auto;
      border: 1px solid var(--border); border-radius: var(--r-lg);
      background: var(--surface);
    }
    /* min-width so the 10 columns scroll on phones/tablets instead of crushing
       to a few px each (table-layout:fixed would otherwise shrink them). */
    .jlt-table { width: 100%; min-width: 800px; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
    .jlt-thead-row { border-bottom: 1px solid var(--border); background: var(--surface-2); }
    .jlt-th {
      padding: 8px 10px; font-size: 11px; font-weight: 600;
      text-transform: uppercase; letter-spacing: 0.06em;
      color: var(--fg-dim); text-align: left; overflow: hidden;
    }
    .jlt-th:nth-child(1)  { width: 9%;  }
    .jlt-th:nth-child(2)  { width: 14%; }
    .jlt-th:nth-child(3)  { width: 12%; }
    .jlt-th:nth-child(4)  { width: 9%;  }
    .jlt-th:nth-child(5)  { width: 9%;  }
    .jlt-th:nth-child(6)  { width: 9%;  }
    .jlt-th:nth-child(7)  { width: 12%; }
    .jlt-th:nth-child(8)  { width: 10%; }
    .jlt-th:nth-child(9)  { width: 7%;  }
    .jlt-th:nth-child(10) { width: 9%;  }
    .jlt-th-r { text-align: right; }
    .jlt-row {
      border-bottom: 1px solid var(--border); cursor: pointer;
      transition: background .1s;
    }
    .jlt-row:last-child { border-bottom: 0; }
    .jlt-row:hover { background: var(--surface-2); }
    .jlt-row:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
    .jlt-archived { opacity: 0.55; }
    .jlt-flagged td:first-child { border-left: 2px solid var(--warn); }
    /* Bulk-select checkbox column */
    .jlt-check { width: 40px; text-align: center; padding: 0 6px 0 12px; vertical-align: middle; }
    .jl-check { width: 15px; height: 15px; cursor: pointer; accent-color: var(--accent); vertical-align: middle; margin: 0; }
    .jlt-row.jlt-selected { background: oklch(from var(--accent) l c h / 0.08); }
    .jlt-row.jlt-selected:hover { background: oklch(from var(--accent) l c h / 0.12); }
    .jlt-num    { padding: 10px 10px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .jlt-name   { padding: 10px 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .jlt-vendor { padding: 10px 10px; color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .jlt-amt    { padding: 10px 10px; text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .jlt-pct    { padding: 10px 10px; }
    .jlt-pct-wrap { display: flex; align-items: center; gap: 6px; }
    .jlt-pct-bar  { flex: 1; height: 5px; background: var(--surface-3); border-radius: 999px; overflow: hidden; min-width: 0; }
    .jlt-pct-fill { height: 100%; border-radius: 999px; }
    .jlt-pct-val  { font-size: 11px; font-weight: 600; white-space: nowrap; flex-shrink: 0; width: 36px; text-align: right; }
    .jlt-status { padding: 10px 10px; overflow: hidden; }
    .jlt-status-pill {
      display: inline-flex; align-items: center; gap: 5px;
      font-size: 12px; font-weight: 500; color: var(--sc);
      overflow: hidden; white-space: nowrap;
    }
    .jlt-sdot { width: 6px; height: 6px; border-radius: 50%; background: var(--sc); flex-shrink: 0; }
    .jlt-flags { padding: 10px 10px; }
    .jlt-flag-chip {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 11px; color: var(--warn); background: var(--warn-bg);
      border: 1px solid oklch(from var(--warn) l calc(c*.5) h / .35);
      padding: 2px 7px; border-radius: 999px;
    }
    .jlt-tag {
      font-size: 10px; padding: 2px 7px; border-radius: 999px; font-weight: 500;
      text-transform: uppercase; letter-spacing: 0.05em; border: 1px solid var(--border);
    }
    .jlt-tag-archived { background: var(--surface-3); color: var(--fg-dim); }
    .jlt-empty { color: var(--fg-dim); }
    .jlt-acts { padding: 7px 8px; display: flex; gap: 3px; align-items: center; }

    /* ── Bulk action bar (floats while a table selection is active) ─── */
    .jl-bulkbar {
      position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
      z-index: 300; display: flex; align-items: center; gap: 16px;
      padding: 9px 10px 9px 18px; border-radius: 999px;
      background: var(--surface); border: 1px solid var(--border-strong);
      box-shadow: var(--shadow-2);
      animation: jl-bulk-up .18s cubic-bezier(.23,1,.32,1);
    }
    @keyframes jl-bulk-up { from { opacity: 0; transform: translate(-50%, 14px); } to { opacity: 1; transform: translate(-50%, 0); } }
    .jl-bb-count { font-size: 13px; color: var(--fg-muted); white-space: nowrap; }
    .jl-bb-count strong { color: var(--fg); font-weight: 700; }
    .jl-bb-actions { display: flex; align-items: center; gap: 6px; }
    .jl-bb-btn {
      appearance: none; border: 1px solid var(--border); background: var(--surface-2);
      border-radius: var(--r); padding: 6px 12px; font: inherit; font-size: 12.5px; font-weight: 500;
      color: var(--fg-muted); cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
      transition: background .12s, color .12s, border-color .12s;
    }
    .jl-bb-btn:hover { background: var(--surface-3); color: var(--fg); border-color: var(--border-strong); }
    .jl-bb-del:hover { color: var(--bad); background: var(--bad-bg); border-color: oklch(from var(--bad) l calc(c*.5) h / .5); }
    .jl-bb-clear {
      appearance: none; background: transparent; cursor: pointer;
      color: var(--fg-dim); display: inline-flex; align-items: center; gap: 4px;
      font: inherit; font-size: 12px; padding: 6px 10px 6px 14px; border: 0;
      border-left: 1px solid var(--border); border-radius: 0;
    }
    .jl-bb-clear:hover { color: var(--fg); }
    @media (max-width: 640px) {
      .jl-bulkbar { width: calc(100vw - 24px); border-radius: var(--r-lg); flex-wrap: wrap; justify-content: center; }
    }

    /* ── Archived section toggle ─────────────────────────────── */
    .jl-archived-section { margin-bottom: 40px; }
    .jl-archived-toggle {
      appearance: none; border: 1px solid var(--border); background: var(--surface-2);
      border-radius: var(--r); padding: 7px 14px; font: inherit; font-size: 12.5px;
      font-weight: 500; color: var(--fg-muted); cursor: pointer;
      display: inline-flex; align-items: center; gap: 7px;
      transition: color .12s, background .12s, border-color .12s;
    }
    .jl-archived-toggle:hover { color: var(--fg); background: var(--surface-3); border-color: var(--border-strong); }

    /* ── Empty / loading states ──────────────────────────────── */
    .jl-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; min-height: 280px; }
    .jl-empty-icon  { color: var(--fg-dim); opacity: 0.3; }
    .jl-empty-title { font-size: 16px; font-weight: 500; color: var(--fg-muted); }
    .jl-empty-sub   { font-size: 13px; color: var(--fg-dim); max-width: 340px; text-align: center; line-height: 1.55; }
    .jl-spin {
      width: 28px; height: 28px; border-radius: 50%;
      border: 2.5px solid var(--border); border-top-color: var(--accent);
      animation: jl-sp .7s linear infinite;
    }
    @keyframes jl-sp { to { transform: rotate(360deg); } }


    /* ── Delete confirm modal ────────────────────────────────── */
    .dcm-overlay {
      position: fixed; inset: 0;
      background: oklch(0 0 0 / 0.45);
      display: grid; place-items: center;
      z-index: 9000;
      animation: dcm-fade .15s ease;
    }
    @keyframes dcm-fade { from { opacity: 0; } to { opacity: 1; } }

    .dcm-dialog {
      background: var(--surface); border: 1px solid var(--border);
      border-radius: var(--r-lg); box-shadow: var(--shadow-2);
      padding: 28px 28px 24px;
      width: min(420px, calc(100vw - 32px));
      display: flex; flex-direction: column; gap: 12px;
      animation: dcm-up .15s ease;
    }
    @keyframes dcm-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

    .dcm-icon-wrap {
      width: 44px; height: 44px; border-radius: 10px;
      background: var(--bad-bg); color: var(--bad);
      border: 1px solid oklch(from var(--bad) l calc(c*.5) h / .3);
      display: grid; place-items: center;
    }
    .dcm-icon-warn {
      background: var(--warn-bg); color: var(--warn);
      border-color: oklch(from var(--warn) l calc(c*.5) h / .3);
    }
    .dcm-title { font-size: 16px; font-weight: 600; letter-spacing: -0.01em; }
    .dcm-body  { font-size: 13px; color: var(--fg-muted); line-height: 1.55; }
    .dcm-num   { font-weight: 600; color: var(--fg); }
    .dcm-name  { color: var(--fg-muted); }

    .dcm-actions {
      display: flex; justify-content: flex-end; gap: 8px;
      margin-top: 4px;
    }
    .dcm-del-btn {
      appearance: none; border: 1px solid oklch(from var(--bad) l calc(c*.5) h / .5);
      background: var(--bad-bg); color: var(--bad);
      border-radius: var(--r); padding: 7px 16px;
      font: inherit; font-size: 13px; font-weight: 500;
      cursor: pointer;
      transition: background .12s, border-color .12s;
    }
    .dcm-del-btn:hover { background: oklch(from var(--bad) l c h / 0.18); border-color: var(--bad); }
    .dcm-archive-btn {
      appearance: none; border: 1px solid oklch(from var(--warn) l calc(c*.5) h / .5);
      background: var(--warn-bg); color: var(--warn);
      border-radius: var(--r); padding: 7px 16px;
      font: inherit; font-size: 13px; font-weight: 500;
      cursor: pointer;
      transition: background .12s, border-color .12s;
    }
    .dcm-archive-btn:hover { background: oklch(from var(--warn) l c h / 0.18); border-color: var(--warn); }

    /* ── Toast notifications ─────────────────────────────────── */
    .toast-container {
      position: fixed; bottom: 24px; right: 24px;
      display: flex; flex-direction: column; gap: 8px;
      z-index: 9999; pointer-events: none;
    }
    .toast {
      padding: 10px 16px; border-radius: var(--r-lg);
      font-size: 13px; font-weight: 500;
      border: 1px solid; white-space: nowrap;
      animation: toast-in .18s ease;
      box-shadow: var(--shadow-2);
    }
    .toast-ok   { background: var(--ok-bg);   color: var(--ok);   border-color: oklch(from var(--ok)  l calc(c*.5) h / .4); }
    .toast-warn { background: var(--warn-bg);  color: var(--warn); border-color: oklch(from var(--warn) l calc(c*.5) h / .4); }
    .toast-bad  { background: var(--bad-bg);   color: var(--bad);  border-color: oklch(from var(--bad)  l calc(c*.5) h / .4); }
    @keyframes toast-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

    @media (max-width: 1100px) {
      .jl-metrics { grid-template-columns: repeat(3, 1fr); }
    }
    @media (max-width: 780px) {
      .jl-metrics { grid-template-columns: repeat(2, 1fr); }
      .jl-toolbar { flex-wrap: wrap; }
      .jl-search-wrap { max-width: 100%; flex: 1 1 100%; }
      .jl-toolbar-right { margin-left: 0; flex: 1 1 100%; justify-content: space-between; }
      /* Portfolio bar: stack the running total over its remaining/over stats. */
      .jl-portfolio { flex-direction: column; align-items: stretch; gap: 16px; }
      .jl-pf-stats { gap: 32px; }
    }
    @media (max-width: 520px) {
      .jl-metrics { grid-template-columns: 1fr; }
    }
  `}</style>
);

// Everything after ConfirmModal is exported for tests only — nothing else in the
// app imports these. Together they are the whole of the dashboard's arithmetic:
// the per-job tile (computeJobStats), the portfolio metric row
// (computePortfolioMetrics), the triage order (sortJobs), the exported
// spreadsheet (jobCSVRow) and the activity-feed clock (tsMillis).
// See tests/invodiff-jobs.test.js.
export {
  JobListView, JobCard, TradeSectionHeader, TradeScopeSelector, ConfirmModal,
  computeJobStats, computePortfolioMetrics, sortJobs, jobCSVRow, JOB_CSV_HEADERS, tsMillis,
  jobStatusLabel, JOB_CARD_STATUS_LABEL, jobStatsFor, rememberJobStats,
};
